import { Component, ViewEncapsulation, ChangeDetectorRef, DestroyRef, DoCheck, inject, OnDestroy, OnInit, ViewChild, ElementRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { UntypedFormGroup, UntypedFormBuilder, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { DateTime as LuxonDateTime } from 'luxon';
import { debounceTime } from 'rxjs/operators';

import { BookJob } from 'src/providers/book-job/book-job';
import { Card } from 'src/providers/card/card';
import { Client } from 'src/providers/client/client';
import { Me } from 'src/providers/me/me';
import { Pros } from 'src/providers/pros/pros';
import { Requests } from 'src/providers/requests/requests';
import { StripeProvider } from 'src/providers/stripe/stripe-provider';
import { Schedule } from 'src/providers/schedule/schedule';
import { CalendarDay } from 'src/providers/calendar/calendar-day';
import { AvailableTimesForRange } from 'src/providers/calendar/available-times-for-range';
import { Homekeeper } from 'src/providers/homekeeper/homekeeper';
import { Addresses } from 'src/providers/customer/addresses';
import { Analytics } from 'src/providers/analytics/analytics';
import { Logger } from 'src/providers/logger';

import { CustomNavController } from 'src/shared/providers/navigation/custom-nav-controller';
import { RightSidePanelService } from 'src/shared/providers/providers/right-side-panel';
import { TidyStorage } from 'src/shared/providers/tidy-storage';
import { WindowService } from 'src/shared/providers/window.service';
import { Util } from 'src/shared/util/util';
import { createThenCancelOriginal, createdBookingId } from 'src/shared/util/reschedule-job';

import { Loading } from 'src/shared/components/loading/loading';
import { DynamicBookingFormComponent } from 'src/shared/components/dynamic-booking-form/dynamic-booking-form.component';

import { AddressModel } from 'src/models/address.model';
import { ProModel, ProTeamServices, AvailableTimesModel } from 'src/models/book-job.model';
import { RadioButtonModel } from 'src/tidy-ui-components/components/form/radio-button/radio-button.model';

import { TimeRanges } from 'src/providers/time-ranges/time-ranges';

import { ParseDateTimePipe } from 'src/shared/pipes/parse-date.pipe';

import { bookJobConfigurations } from 'src/pages/booking/book-job/book-job-configurations';
import { getCardOptions } from 'src/old-pages/booking/confirm/confirm-card-options';
import { PropertyOptionsPage } from 'src/pages/more/edit-address/property-options/property-options';

import { buildCategoryHierarchy, MainCategoryDefinition, SubcategoryDefinition } from 'src/shared/constants/service-category-hierarchy';
import { PosthogService } from 'src/shared/services/posthog.service';
import { ThemeService } from 'src/shared/services/theme.service';
import { BookService } from 'src/shared/services/book.service';
import { BOOK_PAGE_VARIANTS } from 'src/shared/constants/feature-flags';

import { CookieService } from 'ngx-cookie';
import validationUtils from 'src/shared/util/validation-utils';
import { parsePhoneNumber } from 'src/tidy-ui-components/components/form/input-international-phone/international-number.utils';
import { TranslationPipe } from 'src/shared/pipes/translation.pipe';

@Component({
  templateUrl: 'book.html',
  styleUrls: ['./book.scss'],
  encapsulation: ViewEncapsulation.None,
  selector: 'book-page',
  standalone: false
})
export class BookPage implements OnInit, OnDestroy, DoCheck {
  private destroyRef = inject(DestroyRef);

  // --- Step management ---
  currentStep = 1;
  highestStepReached = 1;

  // --- Step 2 prefetch ---
  private prefetchPromise: Promise<void> | null = null;
  private prefetchCategoryId: number | null = null;
  private prefetchComplete = false;

  // --- Step 3 prefetch (billing data) ---
  private billingDataPromise: Promise<any> | null = null;

  // --- Address selection ---
  addresses: AddressModel[] = [];
  addressFilterItems: { value: any; viewValue: string }[] = [];

  // --- Pro selector ---
  addressPros: any[] = [];
  proSelectItems: { value: any; viewValue: string }[] = [];
  selectedProId: any = 'new';
  selectedProName = '';
  allMainCategories: MainCategoryDefinition[] = [];

  // --- Step 1: Category selection ---
  serviceTypeItems: any[] = [];
  mainCategories: MainCategoryDefinition[] = [];
  filteredMainCategories: MainCategoryDefinition[] = [];
  selectedMainCategory: MainCategoryDefinition | null = null;
  filteredSubcategories: SubcategoryDefinition[] = [];
  selectedSubcategory: SubcategoryDefinition | null = null;
  selectedCategory: any;
  categorySearchQuery = '';

  // --- Step 2: Service selection ---
  isHouseCleaning = false;
  hideServiceCards = false;
  bookingForms: any[] = null;
  noBookingFormsAvailable = false;
  detailsSummaryItems: { label: string; value?: string; imageUrls?: string[] }[] = [];
  private savedFormStateByCategory: Map<number, {
    formValues: any;
    attachments: any[];
    checkboxes: any[];
  }> = new Map();
  private cachedBookingFormData: any = null;
  @ViewChild('dynamicBookingForm') dynamicBookingForm: DynamicBookingFormComponent;
  address: AddressModel;
  bookingType: string;
  configuration: any;
  services: ProTeamServices[] = [];
  selectedServiceData: ProTeamServices;
  selectedFrequency = 'Frequency';
  selectedBillingType: string;
  selectedDuration: any;
  selectedPrice: any;
  selectedService = 'Service';
  selectedServiceImage: string;
  selectedPrivatePro: boolean;
  choseService = false;
  choseFrequency = false;
  prosRatingsOptions: any[] = [];
  pros: ProModel[] = [];
  selectedPro: any;
  selectedStarRatingFilter = 'all';
  hasResolvedStarRatingFilter = false;
  frequencyOrder: number[] = [0, 1, 2, 3, 4, 5];
  starRatingFilterItems = [
    { viewValue: '4.6+ Star Rating', value: 'prioritized_v2:4_6' },
    { viewValue: '4.2+ Star Rating', value: 'prioritized_v2:4_2' },
    { viewValue: '4+ Star Rating', value: '4' },
    { viewValue: 'Any Rating', value: 'all' },
  ];
  proSelected = false;
  proName: string | null;
  proTeamName: string | null;
  flowType: string;
  categoryId: any;
  planId: any;
  bookingId: any;
  isRentalClient: boolean;
  hasNonTrialPaidSubscription: boolean;
  isUpdatingSubscription: boolean;
  currentSubscription: any;
  priceExperiment: any;
  bookingExperiment: string | null;
  subscriptionExperiment: any;
  customFlowName: string | null;
  customFlowSmallImage: string | null;
  customFlowDiscount: number;
  frequencyValue: any;
  startDay: any;
  endDay: any;
  hasValidCC: boolean = localStorage.getItem('hasCard') === 'true';
  loaded = false;
  initialized = false;
  errorMessage: string;
  newValue: string;
  showMoreServices: boolean;
  showSavingsText: boolean;
  isZeroCostService = false;
  notChoseServiceSentence: string;
  form: UntypedFormGroup;
  giftForm: UntypedFormGroup;
  dialogParams: any;
  isRightSideContent = true;

  // --- Step 4: Time selection ---
  dateRangeStart: string | null = null;
  dateRangeEnd: string | null = null;
  timeRangeStart: string | null = null;
  timeRangeEnd: string | null = null;
  dateTimeSummary = '';
  probabilityData: any = null;
  didSelectAllRangeTimes = false;
  isLoadingProbability = false;
  minDate: string;

  // --- Step 4: Select-time mode (when showSelectTime=true) ---
  dateTimes: Array<{ date: string; times: Array<{ value: string; viewValue: string; pros: number[] }> }> = [];
  isLoadingTimes = false;
  noAvailableTimes = false;
  selectedTimeValue: string | null = null;
  selectedTimePros: number[] = [];
  selectedTimeDate: string | null = null;
  timeCardsCollapsed = false;
  showProRequestView = false;
  showRequestTimeView = false;
  privateJobDates: Array<{ value: string; viewValue: string }> = [];
  isLoadingPrivateTimes = false;
  minRateToBookWithPro: number | null = null;
  proNotBookableReason: any = null;
  notBookableHeader = '';

  // --- Multi-time selection (2x/3x per week) ---
  moreTimesPerWeekSelected = false;
  allTimesSelected: Array<{ value: string; viewValue: string; pros: number[] }> = [];
  multiTimeStep = 0;
  filteredDateTimesForStep: Array<{ date: string; times: Array<{ value: string; viewValue: string; pros: number[] }> }> = [];

  // --- Tab bar scroll ---
  @ViewChild('categoryTabBar', { static: false }) categoryTabBar: ElementRef;
  showTabLeftArrow = false;
  showTabRightArrow = true;

  // --- Step 3: Confirm/Payment ---
  @ViewChild('cardInfo', { static: false }) cardInfo: ElementRef;
  cardEl: any;
  cardHandler = this.onChange.bind(this);
  creditBalance: number;
  employerName: string | null;
  chargeBeforePrice: number;
  alreadyHasChargeAfterSetting: boolean;
  displayPrice: any;
  paymentOptions: Array<{ viewValue: string; value: string; savingsTag?: string; extraText?: string }> = [];
  buttonTitle: string;
  termsChecked = true;
  termsError: boolean;
  giftCode: any;
  giftCodeForm: boolean;
  giftCodeErrorMessage: string;
  referralCode: boolean;
  submitted = false;
  viewData: any;
  bookData: any;
  showSameDayTimeAlert: boolean;
  sameDayPrice: any;
  countryCode: string;
  tidyPlusSubscription: any;
  showSelectTime: boolean;
  showNonCleaningServices: boolean;
  pageVariant: string | null = null;
  hasTrackedDateTimeReached = false;
  hasTrackedConfirmReached = false;
  boundConfirmBooking = this.confirmBooking.bind(this);

  // --- Computed template state (updated via ngDoCheck / mutation sites) ---
  stepTitle = 'Book Job';
  locationStepValid = false;
  categoryStepValid = false;
  step2Valid = false;
  step3Valid = false;
  hasSelectedPro = false;
  inMultiTimeMode = false;
  inRescheduleMode = false;
  inProRequestMode = false;
  multiTimeLabelText = '';
  multiTimeSummaryTexts: string[] = [];
  readonly multiTimeOrdinalLabels = ['First Time: ', 'Second Time: ', 'Third Time: '];
  savingsTextValue: string | null = null;
  servicePriceTexts: Record<string, string> = {};
  servicePopularFlags: Record<string, boolean> = {};
  proRequestEndTimeOptions: any[][][] = [];
  priceBodyText = '';
  satisfactionGuaranteeText = '';
  customizableText = '';

  // --- Pro request time selection (checkbox day + time range UI) ---
  proRequestDays: any[] = [];
  proRequestWeekDayCheckBox: Array<UntypedFormGroup> = [];
  proRequestTimeRangeForms: Array<Array<UntypedFormGroup>> = Array(28).fill(null).map(() => []);
  proRequestTimeOpts: any[] = [];
  proRequestTodayStartOpts: any[] = [];
  proRequestSelectedTimes: any[] = [];
  proRequestNotSelectedTimesError = false;

  constructor(
    private addressesService: Addresses,
    private analytics: Analytics,
    private availableTimesForRange: AvailableTimesForRange,
    public bookJobService: BookJob,
    private calendarDay: CalendarDay,
    private card: Card,
    private changeDetectorRef: ChangeDetectorRef,
    private client: Client,
    private cookieService: CookieService,
    private fb: UntypedFormBuilder,
    private homekeeper: Homekeeper,
    private logger: Logger,
    public me: Me,
    private navCtrl: CustomNavController,
    private posthogService: PosthogService,
    private bookService: BookService,
    private route: ActivatedRoute,
    private prosService: Pros,
    private requests: Requests,
    private rightSidePanelService: RightSidePanelService,
    private schedule: Schedule,
    private storage: TidyStorage,
    private stripeProvider: StripeProvider,
    private timeRanges: TimeRanges,
    private themeService: ThemeService,
    public util: Util,
    public windowService: WindowService
  ) {
    this.form = this.fb.group({
      // Address
      addressId: [''],
      // Step 2
      proSelect: ['new'],
      proOption: ['', Validators.required],
      service: ['', Validators.required],
      frequency: [''],
      // Step 3
      timeType: [''],
      confirmedDate: [''],
      confirmedTime: [''],
      noEarlierTime: [''],
      noLaterTime: [''],
      time: [''],
      privateDate: [''],
      rangeStartDate: [''],
      rangeEndDate: [''],
      // Step 4
      chargeType: [''],
      firstName: [''],
      lastName: [''],
      email: [''],
      phone: ['']
    });
    this.giftForm = this.fb.group({
      giftCode: ['']
    });
    this.giftForm.valueChanges
      .pipe(debounceTime(1000))
      .subscribe(() => this.submitGiftCode());
  }

  async ngOnInit() {
    try {
      this.isRightSideContent = await this.storage.retrieve('dialog-right-side-open') || false;
      this.dialogParams = await this.storage.retrieve('dialog-params') || {};
      const routeVariant = this.route.snapshot.params['variant'];
      if (routeVariant && (BOOK_PAGE_VARIANTS as readonly string[]).includes(routeVariant)) {
        this.dialogParams.pageVariant = routeVariant;
      }
      this.getMultiVariantTestInfo();
      this.bookingType = this.dialogParams?.bookingType || 'add_job';
      this.prefetchBillingData();
      this.rightSidePanelService.setDialogPageTitle(this.getStepTitle());
      await this.loadAddresses();
      // preSelectHouseCleaning is passed by book.service.ts for the
      // 'new-view-collapsed' variant: keep the full Service step visible
      // (so the user can navigate back to change category) but auto-pick
      // House Cleaning and land on Step 3 (Details).
      const preSelectHouseCleaning = !!this.dialogParams?.preSelectHouseCleaning;
      if (this.address) {
        if (!this.showNonCleaningServices || preSelectHouseCleaning) {
          this.currentStep = 3;
          this.highestStepReached = 3;
        } else {
          this.currentStep = 2;
          this.highestStepReached = 2;
        }
      }
      await this.loadServiceCategories();
      if (!this.showNonCleaningServices || preSelectHouseCleaning) {
        this.autoSelectCleaningCategory();
      }
      if (this.address) {
        await this.loadProsForAddress();
      }
      if (this.isReschedule()) {
        await this.initRescheduleFlow();
      }
      this.initialized = true;
      if (!this.isReschedule()) {
        this.posthogService.capture('booking_view_entered', {
          variant: this.pageVariant,
          has_credit_card: this.hasValidCC,
          booking_type: this.bookingType,
          is_desktop: this.windowService.isDesktopRes,
          is_house_cleaning: this.isHouseCleaning,
        });
        this.analytics.trackInitiateCheckout();
      }
    } catch (err) {
      this.initialized = true;
      this.errorMessage = err?.error?.message ?? err?.message;
      this.util.showError(this.errorMessage);
      this.logger.error(err);
    }
  }

  private prefetchBillingData() {
    const userRole = localStorage.getItem('user_role');
    if (userRole !== 'member' && !localStorage.getItem('creditBalance')) {
      this.billingDataPromise = this.client.getBillingData();
    }
  }

  getMultiVariantTestInfo() {
    this.pageVariant = this.dialogParams?.pageVariant || null;
    this.showSelectTime = this.pageVariant === 'cleaning-select-time' || this.pageVariant === 'all-categories-select-time';
    this.showNonCleaningServices = this.pageVariant === 'all-categories-any-time' || this.pageVariant === 'all-categories-select-time';
  }

  ngOnDestroy() {
    this.cardEl?.removeEventListener('change', this.cardHandler);
    this.cardEl?.destroy();
  }

  ngDoCheck(): void {
    this.hasSelectedPro = this.selectedProId !== 'new';
    this.inMultiTimeMode = this.moreTimesPerWeekSelected && !this.showProRequestView;
    this.inRescheduleMode = this.bookingType === 'reschedule_job' || this.bookingType === 'reschedule_plan';
    this.inProRequestMode = this.selectedProId !== 'new' && this.showProRequestView;
    this.locationStepValid = !!this.address;
    this.categoryStepValid = !!this.selectedCategory;
    this.stepTitle = this.getStepTitle();
    this.multiTimeLabelText = this.getMultiTimeLabel();
    this.step2Valid = this.isStep2Valid();
    this.step3Valid = this.isStep3Valid();
  }

  // =============================================
  // Step Navigation
  // =============================================

  goToStep = (step: number) => {
    if (step === this.currentStep) {return;}
    if (!this.showNonCleaningServices && step === 2) {return;}
    if (step < this.currentStep || step <= this.highestStepReached) {
      if (this.currentStep === 3) {
        if (this.dynamicBookingForm?.isReady) {
          this.cachedBookingFormData = this.dynamicBookingForm.buildBookingFormData();
        }
        this.saveBookingFormState();
      }
      this.currentStep = step;
      if (step === 5) {
        this.prepareStep3Data();
      }
      if (step === 4) {
        if (this.showProRequestView && this.proRequestDays.length === 0) {
          this.initProRequestDays();
        }
        if (!this.showProRequestView && (this.moreTimesPerWeekSelected || this.hasSpecificPro() || this.showSelectTime) && this.dateTimes.length === 0) {
          if (this.showSelectTime && this.selectedProId === 'new') {
            this.resolveStarRatingAndFetchTimes();
          } else {
            this.fetchAvailableTimes();
          }
        }
        if (this.isMultiTimeMode() && this.dateTimes.length > 0 && this.allTimesSelected.length > 0) {
          this.updateFilteredDateTimesForMultiStep();
        }
      }
      if (step === 3 && this.savedFormStateByCategory.has(this.categoryId)) {
        setTimeout(() => this.restoreBookingFormState());
      }
      this.changeDetectorRef.detectChanges();
    }
  };

  goNext = async () => {
    if (this.currentStep === 1 && this.isLocationStepValid()) {
      if (!this.showNonCleaningServices) {
        if (!this.selectedCategory) {
          this.autoSelectCleaningCategory();
        }
        if (this.prefetchPromise && !this.prefetchComplete) {
          this.rightSidePanelService.headerSpinnerVisible$.next(true);
          try {
            await this.prefetchPromise;
          } catch (err) {
            this.errorMessage = err?.error?.message ?? err?.message;
            this.util.showError(this.errorMessage);
            this.logger.error(err);
            this.rightSidePanelService.headerSpinnerVisible$.next(false);
            return;
          }
          this.rightSidePanelService.headerSpinnerVisible$.next(false);
        }
        this.currentStep = 3;
        this.highestStepReached = Math.max(this.highestStepReached, 3);
        this.rightSidePanelService.setDialogPageTitle(this.getStepTitle());
        this.changeDetectorRef.detectChanges();
      } else {
        this.currentStep = 2;
        this.highestStepReached = Math.max(this.highestStepReached, 2);
        this.changeDetectorRef.detectChanges();
      }
    } else if (this.currentStep === 2 && this.isCategoryStepValid()) {
      if (!this.selectedCategory.value) {
        this.util.showError('This service is not yet available. Please choose another.');
        return;
      }
      const targetCategoryId = this.selectedCategory.value;
      const targetCategoryKey = this.selectedCategory.key;

      if (this.prefetchComplete && this.prefetchCategoryId === targetCategoryId) {
        this.rightSidePanelService.setDialogPageTitle(this.getStepTitle());
        this.currentStep = 3;
        this.highestStepReached = Math.max(this.highestStepReached, 3);
        this.changeDetectorRef.detectChanges();
        if (this.savedFormStateByCategory.has(this.categoryId)) {
          setTimeout(() => this.restoreBookingFormState());
        }
        return;
      }

      if (!this.prefetchPromise || this.prefetchCategoryId !== targetCategoryId) {
        this.startPrefetch(targetCategoryId, targetCategoryKey);
      }

      this.rightSidePanelService.headerSpinnerVisible$.next(true);
      try {
        await this.prefetchPromise;
        if (this.prefetchCategoryId !== targetCategoryId) {
          return;
        }
        if (this.errorMessage) {
          this.util.showError(this.errorMessage);
          return;
        }
        this.rightSidePanelService.setDialogPageTitle(this.getStepTitle());
        this.currentStep = 3;
        this.highestStepReached = Math.max(this.highestStepReached, 3);
        this.changeDetectorRef.detectChanges();
        if (this.savedFormStateByCategory.has(this.categoryId)) {
          setTimeout(() => this.restoreBookingFormState());
        }
      } catch (err) {
        this.errorMessage = err?.error?.message ?? err?.message;
        this.util.showError(this.errorMessage);
        this.logger.error(err);
      } finally {
        this.rightSidePanelService.headerSpinnerVisible$.next(false);
      }
    } else if (this.currentStep === 3 && this.isStep2Valid()) {
      if (!this.isHouseCleaning) {
        this.detailsSummaryItems = this.buildDetailsSummaryItems();
      }
      if (this.dynamicBookingForm?.isReady) {
        this.cachedBookingFormData = this.dynamicBookingForm.buildBookingFormData();
      }
      this.saveBookingFormState();
      this.currentStep = 4;
      this.highestStepReached = Math.max(this.highestStepReached, 4);
      if (!this.isReschedule() && !this.hasTrackedDateTimeReached) {
        this.posthogService.capture('booking_date_time_reached', {
          variant: this.pageVariant,
          has_credit_card: this.hasValidCC,
          booking_type: this.bookingType,
          is_desktop: this.windowService.isDesktopRes,
          is_house_cleaning: this.isHouseCleaning,
        });
        this.hasTrackedDateTimeReached = true;
      }
      this.showProRequestView = false;
      this.showRequestTimeView = false;
      if (this.moreTimesPerWeekSelected || this.hasSpecificPro() || this.showSelectTime) {
        if (this.showSelectTime && this.selectedProId === 'new') {
          this.resolveStarRatingAndFetchTimes();
        } else {
          this.fetchAvailableTimes();
        }
      }
      this.changeDetectorRef.detectChanges();
    } else if (this.currentStep === 4 && this.isStep3Valid()) {
      this.currentStep = 5;
      this.highestStepReached = Math.max(this.highestStepReached, 5);
      if (!this.isReschedule() && !this.hasTrackedConfirmReached) {
        this.posthogService.capture('booking_confirm_reached', {
          variant: this.pageVariant,
          has_credit_card: this.hasValidCC,
          booking_type: this.bookingType,
          is_desktop: this.windowService.isDesktopRes,
          is_house_cleaning: this.isHouseCleaning,
        });
        this.hasTrackedConfirmReached = true;
      }
      this.prepareStep3Data();
      this.changeDetectorRef.detectChanges();
    }
  };

  goBack = () => {
    if (this.currentStep > 1) {
      if (!this.showNonCleaningServices && this.currentStep === 3) {
        this.currentStep = 1;
      } else {
        this.currentStep--;
      }
      if (this.currentStep === 4 && this.isMultiTimeMode()) {
        this.multiTimeStep = Math.min(this.allTimesSelected.length, this.requiredTimeCount() - 1);
      }
      if (this.currentStep === 3 && this.savedFormStateByCategory.has(this.categoryId)) {
        this.changeDetectorRef.detectChanges();
        setTimeout(() => this.restoreBookingFormState());
      }
      this.changeDetectorRef.detectChanges();
    }
  };

  getStepTitle(): string {
    if (this.bookingType === 'reschedule_job') {return 'Reschedule Job';}
    if (this.bookingType === 'reschedule_plan') {return 'Reschedule Plan';}
    if (this.bookingType === 'add_one_time_job') {return 'Add One Time Job';}
    return 'Book Job';
  }

  isReschedule(): boolean {
    return this.bookingType === 'reschedule_job' || this.bookingType === 'reschedule_plan';
  }

  private async initRescheduleFlow(): Promise<void> {
    const hkId = this.dialogParams?.hkId;
    const filterByPro = this.dialogParams?.filterByPro;
    if (filterByPro && hkId && this.addressPros?.length) {
      const matchingPro = this.addressPros.find((p) => p.id === hkId);
      if (matchingPro) {
        this.selectedProId = hkId;
        this.selectedProName = matchingPro.name || '';
        this.form.patchValue({ proSelect: hkId });
      }
    }

    const categoryId = this.dialogParams?.categoryId;
    if (categoryId) {
      const subcategory = this.allMainCategories
        .flatMap((cat) => cat.subcategories)
        .find((sub) => {
          const apiCat = this.serviceTypeItems.find((s) => s.key === sub.apiKey);
          return apiCat?.value === categoryId;
        });
      if (subcategory) {
        this.selectSubcategory(subcategory);
        if (this.prefetchPromise) {
          await this.prefetchPromise;
        }
        this.currentStep = 3;
        this.highestStepReached = 3;
      }
    }
  }

  isCategoryStepValid(): boolean {
    return !!this.selectedCategory;
  }

  isStep2Valid(): boolean {
    if (this.isHouseCleaning && !this.hideServiceCards) {
      return this.choseService && this.choseFrequency && !!this.selectedServiceData;
    }
    if (this.hideServiceCards && this.bookingForms) {
      if (!this.dynamicBookingForm) {return false;}
      return this.dynamicBookingForm.isFormValid();
    }
    if (this.hideServiceCards) {
      return !!this.selectedServiceData;
    }
    if (!this.isHouseCleaning) {
      if (this.noBookingFormsAvailable) {return false;}
      if (!this.dynamicBookingForm) {return false;}
      return this.dynamicBookingForm.isFormValid();
    }
    return false;
  }

  isStep3Valid(): boolean {
    if (this.isProRequestMode()) {
      return this.proRequestSelectedTimes.length > 0 && this.proRequestValidForms();
    }
    if (this.isMultiTimeMode()) {
      return this.allTimesSelected.length >= this.requiredTimeCount();
    }
    if ((this.hasSpecificPro() || this.showSelectTime) && !this.showRequestTimeView) {
      return !!this.selectedTimeValue;
    }
    return !!this.dateRangeStart && !!this.timeRangeStart;
  }

  // =============================================
  // Step 1: Category Selection
  // =============================================

  async loadAddresses() {
    try {
      this.addresses = await this.client.getAddresses();
      this.addressFilterItems = this.addresses.map((addr) => ({
        value: addr.id,
        viewValue: addr.address_name || addr.address1 || `Address ${addr.id}`
      }));
      const paramAddress = this.dialogParams?.address;
      const selectedId = paramAddress?.id || this.addresses[0]?.id;
      this.address = this.addresses.find((a) => a.id === selectedId) || this.addresses[0];
      if (this.address) {
        this.form.patchValue({ addressId: this.address.id });
        this.storage.save('address', this.address);
        localStorage.setItem('addressId', this.address.id.toString());
      }
    } catch (err) {
      this.logger.error(err, 'book-load-addresses');
    }
  }

  onAddressChange = (event: any) => {
    const selectedId = event?.value ?? event;
    this.address = this.addresses.find((a) => a.id === selectedId);
    if (this.address) {
      this.form.patchValue({ addressId: this.address.id });
      this.storage.save('address', this.address);
      localStorage.setItem('addressId', this.address.id.toString());
    }
    this.resetTimeSelectionState();
    this.highestStepReached = 1;
    this.invalidatePrefetch();
    this.selectedProId = 'new';
    this.form.patchValue({ proSelect: 'new' });
    if (this.selectedCategory?.value) {
      this.startPrefetch(this.selectedCategory.value, this.selectedCategory.key);
    }
    this.loadProsForAddress();
    this.changeDetectorRef.detectChanges();
    if (this.currentStep === 1 && this.isLocationStepValid()) {
      this.goNext();
    }
  };

  isLocationStepValid(): boolean {
    return !!this.address;
  }

  async loadProsForAddress() {
    try {
      const allProsForAddress = await this.prosService.getAllProsByAddress(this.address.id);
      this.addressPros = allProsForAddress;
      const serviceTypeId = this.selectedCategory?.value;
      const filteredPros = serviceTypeId
        ? this.addressPros.filter(pro =>
            (pro.address_homekeeper_priorities ?? []).some(p => p.service_type_id === serviceTypeId)
          )
        : this.addressPros;
      this.proSelectItems = [
        { viewValue: 'Find New Pro', value: 'new' },
        ...filteredPros.map((p) => ({ viewValue: p.name, value: p.id }))
      ];
      this.selectedProId = 'new';
      this.form.patchValue({ proSelect: 'new' });
    } catch (err) {
      this.addressPros = [];
      this.proSelectItems = [{ viewValue: 'Find New Pro', value: 'new' }];
      this.selectedProId = 'new';
    }
  }

  onProSelected = (event: any) => {
    const value = event?.value ?? event;
    this.selectedProId = value;
    this.selectedProName = value !== 'new'
      ? (this.addressPros.find(p => p.id === value)?.name || '')
      : '';
    this.showProRequestView = false;
    this.selectedSubcategory = null;
    this.selectedCategory = null;
    this.invalidatePrefetch();

    if (value === 'new') {
      this.mainCategories = this.allMainCategories;
      this.filteredMainCategories = this.allMainCategories;
      this.selectedMainCategory = this.allMainCategories[0] || null;
      this.filteredSubcategories = this.selectedMainCategory?.subcategories ?? [];
    } else {
      const pro = this.addressPros.find((p) => p.id === value);
      const serviceTypeIds = (pro?.address_homekeeper_priorities ?? []).map((p) => p.service_type_id);
      const serviceKeys = new Set(
        serviceTypeIds
          .map((id) => this.serviceTypeItems.find((s) => s.value === id)?.key)
          .filter(Boolean)
      );
      this.filteredSubcategories = this.allMainCategories
        .flatMap((cat) => cat.subcategories)
        .filter((sub) => serviceKeys.has(sub.apiKey));
      if (this.filteredSubcategories.length === 1) {
        this.selectSubcategory(this.filteredSubcategories[0]);
      }
    }
    this.categorySearchQuery = '';
    this.changeDetectorRef.detectChanges();
  };

  async loadServiceCategories() {
    try {
      const categories = await this.prosService.getServiceCategories();
      this.serviceTypeItems = categories.map((category) => ({
        ...category,
        icon: category.icon_url,
        viewValue: category.name === 'Regular Cleaning' ? 'Cleaning' : category.name,
        value: category.id
      }));
      const hierarchy = buildCategoryHierarchy(categories);
      this.allMainCategories = hierarchy;
      this.mainCategories = hierarchy;
      this.filteredMainCategories = hierarchy;
      this.selectedMainCategory = hierarchy[0] || null;
      this.filteredSubcategories = this.selectedMainCategory?.subcategories ?? [];
      if (!this.selectedSubcategory) {
        const houseCleaning = hierarchy
          .flatMap(cat => cat.subcategories)
          .find(sub => sub.apiKey === 'regular_cleaning');
        if (houseCleaning) {
          this.selectSubcategory(houseCleaning, false);
        }
      }
    } catch (err) {
      this.errorMessage = err?.error?.message ?? err?.message;
      this.util.showError(this.errorMessage);
      this.logger.error(err);
    }
  }

  selectMainCategory = (category: MainCategoryDefinition) => {
    this.selectedMainCategory = category;
    this.selectedSubcategory = null;
    this.selectedCategory = null;
    if (this.categorySearchQuery?.trim()) {
      const lowerQuery = this.categorySearchQuery.toLowerCase();
      const catNameMatches = category.name.toLowerCase().includes(lowerQuery);
      this.filteredSubcategories = catNameMatches
        ? category.subcategories
        : category.subcategories.filter(
            (sub) => sub.name.toLowerCase().includes(lowerQuery)
          );
    } else {
      this.filteredSubcategories = category.subcategories;
    }
    this.changeDetectorRef.detectChanges();
  };

  scrollTabBarRight = () => {
    this.categoryTabBar?.nativeElement?.scrollBy({ left: 200, behavior: 'smooth' });
  };

  scrollTabBarLeft = () => {
    this.categoryTabBar?.nativeElement?.scrollBy({ left: -200, behavior: 'smooth' });
  };

  onTabBarScroll = () => {
    const el = this.categoryTabBar?.nativeElement;
    if (!el) {return;}
    this.showTabLeftArrow = el.scrollLeft > 10;
    this.showTabRightArrow = el.scrollLeft < el.scrollWidth - el.clientWidth - 10;
  };

  selectSubcategory = (subcategory: SubcategoryDefinition, autoAdvance = true) => {
    this.selectedSubcategory = subcategory;
    const apiCategory = this.serviceTypeItems.find(
      (item) => item.key === subcategory.apiKey
    );
    this.selectedCategory = {
      value: apiCategory?.value ?? null,
      viewValue: subcategory.name,
      icon: subcategory.icon,
      key: subcategory.apiKey
    };
    this.changeDetectorRef.detectChanges();
    if (this.selectedCategory.value) {
      this.startPrefetch(this.selectedCategory.value, this.selectedCategory.key);
    }
    if (autoAdvance && this.currentStep === 2) {
      this.goNext();
    }
  };

  filterCategories = (query: string) => {
    this.categorySearchQuery = query;
    this.applySearchFilter(query);
    this.changeDetectorRef.detectChanges();
  };

  private applySearchFilter(query: string) {
    if (!query?.trim()) {
      this.filteredMainCategories = this.mainCategories;
      this.filteredSubcategories = this.selectedMainCategory.subcategories;
      return;
    }
    const lowerQuery = query.toLowerCase();
    this.filteredMainCategories = this.mainCategories.filter(
      (cat) => cat.name.toLowerCase().includes(lowerQuery) ||
        cat.subcategories.some((sub) => sub.name.toLowerCase().includes(lowerQuery))
    );
    // Auto-select first matching category if current selection is filtered out
    if (this.filteredMainCategories.length > 0 &&
        !this.filteredMainCategories.some((cat) => cat.id === this.selectedMainCategory?.id)) {
      this.selectedMainCategory = this.filteredMainCategories[0];
    }
    // Show subcategories for the selected main category, filtered by query
    const cat = this.selectedMainCategory;
    if (cat) {
      const catNameMatches = cat.name.toLowerCase().includes(lowerQuery);
      this.filteredSubcategories = catNameMatches
        ? cat.subcategories
        : cat.subcategories.filter(
            (sub) => sub.name.toLowerCase().includes(lowerQuery)
          );
    } else {
      this.filteredSubcategories = [];
    }
  }

  // --- Cleaning-only mode methods (when showNonCleaningServices=false) ---

  private autoSelectCleaningCategory() {
    const cleaningSubcategory = this.allMainCategories
      .flatMap(cat => cat.subcategories)
      .find(sub => sub.apiKey === 'regular_cleaning');
    if (cleaningSubcategory) {
      this.selectSubcategory(cleaningSubcategory);
    }
  }

  onProSelectedInDetailsStep = async (event: any) => {
    const value = event?.value ?? event;
    this.selectedProId = value;
    this.selectedProName = value !== 'new'
      ? (this.addressPros.find(p => p.id === value)?.name || '')
      : '';
    this.showProRequestView = false;
    this.dateTimes = [];
    this.selectedTimeValue = null;
    this.selectedTimePros = [];
    this.selectedTimeDate = null;
    this.timeCardsCollapsed = false;
    this.invalidatePrefetch();
    this.choseService = false;
    this.choseFrequency = false;
    this.selectedServiceData = null;
    this.loaded = false;
    this.autoSelectCleaningCategory();
    if (this.prefetchPromise) {
      this.rightSidePanelService.headerSpinnerVisible$.next(true);
      try {
        await this.prefetchPromise;
      } catch (err) {
        this.errorMessage = err?.error?.message ?? err?.message;
        this.util.showError(this.errorMessage);
        this.logger.error(err);
      } finally {
        this.rightSidePanelService.headerSpinnerVisible$.next(false);
      }
    }
    this.changeDetectorRef.detectChanges();
  };

  // =============================================
  // Step 2: Service Selection (ported from book-job.ts)
  // =============================================

  @Loading('', true)
  async loadStep2Data() {
    try {
      this.rightSidePanelService.setDialogLoading(true);
      this.errorMessage = '';
      this.loaded = false;
      await this.getPageParams();
      await this.initPageData();
      this.configuration = bookJobConfigurations[this.bookingType];
      this.rightSidePanelService.setDialogPageTitle(this.getStepTitle());
      this.loaded = true;
      this.rightSidePanelService.setDialogLoading(false);
    } catch (err) {
      this.errorMessage = err?.error?.message ?? err?.message;
      this.util.showError(this.errorMessage);
      this.logger.error(err);
      this.rightSidePanelService.setDialogLoading(false);
    }
  }

  private async prefetchStep2Data(categoryId: number, categoryKey: string): Promise<void> {
    this.categoryId = categoryId;
    this.isHouseCleaning = categoryKey === 'regular_cleaning';
    this.bookingForms = null;
    this.cachedBookingFormData = null;
    this.noBookingFormsAvailable = false;
    this.hideServiceCards = false;
    this.errorMessage = '';
    this.loaded = false;
    this.choseService = false;
    this.choseFrequency = false;
    this.selectedServiceData = null;

    const selectedPro = this.selectedProId !== 'new'
      ? this.addressPros.find((p) => p.id === this.selectedProId)
      : null;

    if (selectedPro) {
      this.flowType = 'private';
      this.selectedPrivatePro = selectedPro.is_private ?? false;
      localStorage.setItem('proName', selectedPro.name || '');
      this.storage.save('hkId', this.selectedProId);
      this.form.patchValue({ proOption: `homekeeper:${selectedPro.id}` });
    } else {
      this.flowType = null;
      this.selectedPrivatePro = false;
      this.form.patchValue({ proOption: this.selectedStarRatingFilter || 'all' });
    }

    await this.getPageParams();

    if (localStorage.getItem('hasCard') === null) {
      this.hasValidCC = await this.card.hasValidCard();
    }
    const client = await this.client.getClientInfo();
    this.isRentalClient = client.customer_account.account_type === 'rental';
    this.configuration = bookJobConfigurations[this.bookingType];
    this.startDay = await this.calendarDay.getBookJobStartDate(this.address.timezone);
    this.endDay = this.calendarDay.addDays(this.startDay, 28);
    this.minDate = this.startDay;

    if (selectedPro?.is_private) {
      this.services = await this.bookJobService.fetchTeamServices(selectedPro.id, categoryId);
      this.proSelected = true;
    } else if (selectedPro) {
      this.services = await this.bookJobService.fetchRequiredTeamServices(
        `homekeeper:${selectedPro.id}`, true, categoryId
      );
      this.proSelected = true;
    } else {
      this.services = await this.bookJobService.fetchRequiredTeamServices('all', false, categoryId);
      this.proSelected = false;
    }

    const previousServiceKey = this.form.value.service;
    const previousFrequency = this.form.value.frequency;

    if (this.services?.length === 1) {
      this.hideServiceCards = true;
      this.selectService(this.services[0]);
    } else if (this.services?.length > 1) {
      const matchingService = previousServiceKey
        ? this.services.find(s => s.key === previousServiceKey)
        : null;
      if (matchingService) {
        this.selectService(matchingService);
        if (previousFrequency) {
          const freq = matchingService.frequencies?.find(f => f.value === previousFrequency);
          if (freq) {
            this.choseFrequency = true;
            this.selectedFrequency = freq.viewValue;
            this.frequencyValue = freq.value;
            this.form.patchValue({ frequency: freq.value });
            this.selectedPrice = this.selectedServiceData?.price;
            this.showSavingsText = this.checkIfShouldShowSavingsText();
          }
        }
      } else {
        this.form.patchValue({ service: '', frequency: null });
      }
    }

    if (this.configuration?.hide_frequency) {
      this.selectFrequency(this.configuration.frequency_value || { viewValue: 'One Time', value: 'once' });
    }

    if (!this.isHouseCleaning) {
      await this.fetchBookingForms();
    }

    if (this.isRentalClient) {
      this.isUpdatingSubscription = localStorage.getItem('isUpdatingSubscription') === 'true';
      const subscriptionTypes = await this.me.getSubscriptionTypes();
      this.hasNonTrialPaidSubscription = await this.me.checkIfHasNonTrialPaidSubscription(subscriptionTypes);
      this.tidyPlusSubscription = subscriptionTypes.find((type) => type.key === 'tidy_plus_yearly_subscription_not_trial');
      this.storage.save('tidyPlusSubscription', this.tidyPlusSubscription);
      this.currentSubscription = await this.storage.retrieve('currentSubscription');
    }

    this.loaded = true;
    this.updateServicePrices();
  }

  private startPrefetch(categoryId: number, categoryKey: string): void {
    if (this.prefetchCategoryId === categoryId && this.prefetchPromise) {
      return;
    }
    this.prefetchComplete = false;
    this.prefetchCategoryId = categoryId;
    this.prefetchPromise = this.prefetchStep2Data(categoryId, categoryKey)
      .then(() => {
        if (this.prefetchCategoryId === categoryId) {
          this.prefetchComplete = true;
        }
      })
      .catch((err) => {
        if (this.prefetchCategoryId === categoryId) {
          this.errorMessage = err?.error?.message ?? err?.message;
          this.logger.error(err);
        }
      });
  }

  private invalidatePrefetch(): void {
    this.prefetchPromise = null;
    this.prefetchCategoryId = null;
    this.prefetchComplete = false;
  }

  async getPageParams() {
    if (!this.address) {
      this.address = await this.getParam('address');
      if (this.address === null || this.address === undefined) {
        return this.showIssueLoadingPageAlert();
      }
    }
    this.bookingType = await this.getParam('bookingType') || 'add_job';
    if (!this.flowType) {
      this.flowType = await this.getParam('flowType');
    }
    this.planId = await this.getParam('planId');
    this.bookingId = await this.getParam('bookingId');
    if (!this.categoryId) {
      this.categoryId = await this.getParam('categoryId') || 1;
    }
    this.pros = this.getProsParam();
  }

  async getParam(paramName): Promise<any> {
    let param = this.dialogParams?.[paramName];
    if (param === null || param === undefined) {
      param = this.navCtrl.getParam(paramName);
    }
    if (param === null || param === undefined) {
      param = await this.storage.retrieve(paramName);
    }
    this.storage.save(paramName, param);
    return param;
  }

  // Deliberately NOT getParam() — see the same helper in book-job.ts. The 'pros' storage slot
  // is a global, un-scoped key shared with the Messages pages, so reading it here could seed
  // the page with another context's list and suppress the calendar/filters fetch.
  private getProsParam(): any {
    const param = this.dialogParams?.['pros'];
    return (param === null || param === undefined) ? this.navCtrl.getParam('pros') : param;
  }

  async initPageData() {
    try {
      const client = await this.client.getClientInfo();
      this.isRentalClient = client.customer_account.account_type === 'rental';
    } catch (err) {
      this.errorMessage = err?.error?.message ?? err?.message;
      this.logger.error(err);
      this.util.showError(this.errorMessage);
      throw err;
    }
    this.bookingExperiment = localStorage.getItem('bookingExperiment');
    this.priceExperiment = this.bookJobService.getPriceExperiment();
    this.subscriptionExperiment = this.bookJobService.getSubscriptionExperiment();
    this.customFlowName = localStorage.getItem('customFlowName');
    this.customFlowSmallImage = localStorage.getItem('customFlowSmallImage');
    this.customFlowDiscount = parseInt(localStorage.getItem('customFlowDiscount'));
    if (this.customFlowName) {
      this.bookingExperiment = 'consumer-control';
      localStorage.setItem('bookingExperiment', this.bookingExperiment);
    }
    if (this.flowType === 'private') {
      this.proName = localStorage.getItem('proName');
      this.proTeamName = localStorage.getItem('proTeamName');
    }
    this.notChoseServiceSentence = this.bookingType === 'add_one_time_job' ? 'Select service to see times' : 'Select service & frequency to see times';
    this.configuration = bookJobConfigurations[this.bookingType];
    this.startDay = await this.calendarDay.getBookJobStartDate(this.address.timezone);
    this.endDay = this.calendarDay.addDays(this.startDay, 28);
    this.minDate = this.startDay;
    await this.fetchPros();
    const defaultProOption: any = await this.determineDefaultProOption();
    this.form.patchValue({ proOption: defaultProOption });
    await this.fetchServices(defaultProOption);

    if (this.isRentalClient) {
      this.isUpdatingSubscription = localStorage.getItem('isUpdatingSubscription') === 'true';
      const subscriptionTypes = await this.me.getSubscriptionTypes();
      this.hasNonTrialPaidSubscription = await this.me.checkIfHasNonTrialPaidSubscription(subscriptionTypes);
      this.tidyPlusSubscription = subscriptionTypes.find((type) => type.key === 'tidy_plus_yearly_subscription_not_trial');
      this.storage.save('tidyPlusSubscription', this.tidyPlusSubscription);
      this.currentSubscription = await this.storage.retrieve('currentSubscription');
    }
  }

  async determineDefaultProOption() {
    const request: any = {
      frequency: 'once',
      start_date: this.startDay,
      end_date: this.endDay,
      region_id: this.address.region_id,
      address_id: this.address.id,
      service_type_key: 'regular_cleaning.one_hour',
      filter: ''
    };
    try {
      if (this.flowType === 'private') {
        const hkId = await this.getParam('hkId');
        request.filter = `homekeeper:${hkId}`;
      } else {
        const lastPro = this.pros?.find(pro => pro.default);
        if (lastPro?.private) {
          return lastPro.filter;
        }
        const filtersToTry = [
          lastPro?.filter,
          'prioritized_v2:4_6',
          'prioritized_v2:4_2',
          'all'
        ].filter(Boolean);
        for (const filter of filtersToTry) {
          request.filter = filter;
          const response = await this.availableTimesForRange.getAvailableTimes(request);
          if (response.available_times?.length > 0) {
            this.selectedStarRatingFilter = filter;
            break;
          }
        }
        if (!this.selectedStarRatingFilter || this.selectedStarRatingFilter === 'all') {
          this.selectedStarRatingFilter = request.filter;
        }
      }
    } catch (err) {
      this.errorMessage = err?.error?.message ?? err?.message;
      this.logger.error(err, 'book-determine-default-pro');
    }
    return request.filter;
  }

  async fetchPros() {
    try {
      if (!this.pros) {
        this.pros = await this.bookJobService.fetchPros(this.categoryId);
      }
      this.prosRatingsOptions = this.parseProsForFilter();
    } catch (err) {
      this.errorMessage = err?.error?.message ?? err?.message;
      this.util.showError(this.errorMessage);
      this.logger.error(err);
    }
  }

  parseProsForFilter() {
    const array: any = [];
    this.pros?.map(pro => {
      const rating4 = pro.name === '4+ Star Rating';
      const filter = rating4 ? '4' : pro.filter;
      array.push({
        viewValue: pro.name,
        value: filter
      });
    });
    return array;
  }

  async fetchServices(proFilter: string) {
    if (proFilter === '4') {
      proFilter = 'all';
    }
    this.selectedPro = this.prosRatingsOptions.find((option) => proFilter === option.value) || this.prosRatingsOptions[0];
    const selectedProResponseData = this.pros?.find((pro) => pro.name === this.selectedPro?.viewValue);
    const proOptionValue = selectedProResponseData?.filter || proFilter || 'all';
    this.form.patchValue({ proOption: proOptionValue });
    this.selectedPrivatePro = selectedProResponseData?.private ?? false;
    if (this.selectedPrivatePro) {
      const proId = parseInt(proFilter.replace('homekeeper:', ''), 10);
      try {
        this.services = await this.bookJobService.fetchTeamServices(proId, this.categoryId);
        this.proSelected = true;
      } catch (err) {
        this.errorMessage = err?.error?.message ?? err?.message;
        this.util.showError(this.errorMessage);
        this.logger.error(err);
      }
    } else {
      const defaultRatings = ['prioritized_v2:4_6', 'prioritized_v2:4_2', 'all'];
      const filterValue = selectedProResponseData?.filter || proFilter || 'all';
      this.proSelected = !defaultRatings.includes(filterValue);
      try {
        this.services = await this.bookJobService.fetchRequiredTeamServices(filterValue, this.proSelected, this.categoryId);
      } catch (err) {
        this.errorMessage = err?.error?.message ?? err?.message;
        this.util.showError(this.errorMessage);
        this.logger.error(err);
      }
    }
  }

  async fetchBookingForms() {
    try {
      if (!this.services || this.services.length === 0) {
        this.noBookingFormsAvailable = true;
        return;
      }
      this.form.patchValue({ service: this.services[0].key });
      if (!this.selectedServiceData) {
        this.selectedServiceData = this.services[0];
      }
      this.bookingForms = await this.bookJobService.getBookingFormTemplate(this.form.value.service);
      if (!this.bookingForms || this.bookingForms.length === 0 || !this.bookingForms[0]?.inputs?.length) {
        this.noBookingFormsAvailable = true;
        this.bookingForms = null;
      }
    } catch (err) {
      this.noBookingFormsAvailable = true;
      this.bookingForms = null;
    }
  }

  handleServiceClick = (service: ProTeamServices) => {
    if (this.selectedServiceData?.key === service.key) {
      return;
    }
    this.selectService(service);
  };

  selectService(service: ProTeamServices) {
    this.showSavingsText = false;
    this.choseService = true;
    this.selectedServiceData = service;
    this.selectedService = service.title;
    this.selectedBillingType = service.billingType;
    this.selectedServiceImage = service.image;
    this.selectedDuration = service.duration;
    this.form.patchValue({ service: service.key, frequency: null });
    this.selectedFrequency = null;
    this.choseFrequency = false;
    if (this.configuration?.hide_frequency) {
      this.selectFrequency(this.configuration.frequency_value || { viewValue: 'One Time', value: 'once' });
    }
    this.updateServicePrices();
  }

  showAllFrequencies() {
    this.frequencyOrder = [0, 1, 4, 2, 3, 5];
  }

  selectFrequency = (frequency: RadioButtonModel) => {
    this.choseFrequency = true;
    this.selectedFrequency = frequency.viewValue;
    this.frequencyValue = frequency.value;
    this.form.patchValue({ frequency: frequency.value });
    this.selectedPrice = this.selectedServiceData?.price;
    this.showSavingsText = this.checkIfShouldShowSavingsText();
    this.updateServicePrices();
    this.moreTimesPerWeekSelected = ['twice_a_week', 'thrice_a_week'].includes(frequency.value);
    this.allTimesSelected = [];
    this.multiTimeStep = 0;
    this.updateMultiTimeSummaries();
    if (this.currentStep === 3 && this.isStep2Valid()) {
      this.goNext();
    }
  };

  getServicePrice(service: ProTeamServices): string {
    return this.bookJobService.getDisplayPriceForJob(
      this.planId, service, this.selectedFrequency, this.choseFrequency,
      this.bookingType, this.hasNonTrialPaidSubscription, this.priceExperiment,
      this.bookingExperiment, this.customFlowName, this.selectedPrivatePro
    );
  }

  private updateServicePrices(): void {
    this.servicePriceTexts = {};
    this.servicePopularFlags = {};
    this.services?.forEach(service => {
      this.servicePriceTexts[service.key] = this.getServicePrice(service);
      this.servicePopularFlags[service.key] = service.title?.toLowerCase()?.includes('2.5') || false;
    });
  }

  checkIfShouldShowSavingsText(): boolean {
    const isOneTimeJobPlan = this.selectedFrequency === 'One Time' || this.selectedFrequency === 'One Time (+25%)';
    this.savingsTextValue = this.getSavingsText();
    if (!this.savingsTextValue) {
      return false;
    } else if (this.selectedFrequency === 'One Time' && this.choseFrequency && this.subscriptionExperiment === 'showInlineSubscriptionSales' && !this.hasNonTrialPaidSubscription) {
      return false;
    } else if (!this.hasNonTrialPaidSubscription && this.priceExperiment !== 'showCrossedOutDiscount') {
      return false;
    } else if (this.bookingType !== 'add_one_time_job' && !this.selectedPrivatePro) {
      if (this.priceExperiment !== 'showCrossedOutDiscount' && !isOneTimeJobPlan) {
        return false;
      }
      return true;
    }
    return false;
  }

  getSavingsText(): string | null {
    const isOneTimeJobPlan = this.selectedFrequency === 'One Time' || this.selectedFrequency === 'One Time (+25%)';
    const subscriptionType = this.me.getSubscriptionType(this.currentSubscription?.key);
    if (isOneTimeJobPlan && this.hasNonTrialPaidSubscription && this.choseFrequency) {
      return new TranslationPipe().transform('Saving ') + (this.selectedFrequency === 'One Time (+25%)' ? '25%' : '20%') + new TranslationPipe().transform(' with your ') + subscriptionType + new TranslationPipe().transform(' subscription!');
    } else if (isOneTimeJobPlan) {
      if (this.subscriptionExperiment === 'showInlineSubscriptionSales') {
        return 'Upgrade to TIDY+ to save 20%';
      } else if (this.bookingExperiment === 'rental-v1a' || this.bookingExperiment === 'rental-v1b' || this.bookingExperiment === 'consumer-test1') {
        return 'Select a recurring plan to save 20%';
      }
    } else if (this.priceExperiment === 'showCrossedOutDiscount') {
      return 'Saving 20% with a recurring plan!';
    }
    return null;
  }

  showIssueLoadingPageAlert() {
    const shouldShowAllToDosPage = localStorage.getItem('shouldShowAllToDosPage') === 'true';
    this.storage.save('shouldReloadSchedule', 'true');
    const params = {
      header: 'Issue Accessing Page',
      body: 'Please try again from your Schedule and the issue should be resolved.  Thanks for your patience.',
      buttonText: 'Go to Schedule',
      buttonRoute: shouldShowAllToDosPage || this.windowService.isDesktopRes ? 'schedule' : 'schedule-list'
    };
    this.rightSidePanelService.navigateTo('success', params);
    this.rightSidePanelService.setDialogLoading(false);
  }

  // =============================================
  // Step 3: Time Selection
  // =============================================

  private resetTimeSelectionState() {
    this.dateTimes = [];
    this.selectedTimeValue = null;
    this.selectedTimePros = [];
    this.selectedTimeDate = null;
    this.timeCardsCollapsed = false;
    this.isLoadingTimes = false;
    this.noAvailableTimes = false;

    this.proRequestDays = [];
    this.proRequestWeekDayCheckBox = [];
    this.proRequestTimeRangeForms = Array(28).fill(null).map(() => []);
    this.proRequestSelectedTimes = [];
    this.proRequestNotSelectedTimesError = false;
    this.showProRequestView = false;
    this.showRequestTimeView = false;
    this.privateJobDates = [];
    this.isLoadingPrivateTimes = false;

    this.dateRangeStart = null;
    this.dateRangeEnd = null;
    this.timeRangeStart = null;
    this.timeRangeEnd = null;
    this.form.patchValue({ noEarlierTime: null, noLaterTime: null });

    this.dateTimeSummary = '';
    this.probabilityData = null;
    this.didSelectAllRangeTimes = false;
    this.isLoadingProbability = false;
    this.moreTimesPerWeekSelected = false;
    this.allTimesSelected = [];
    this.multiTimeStep = 0;
    this.filteredDateTimesForStep = [];
    this.updateMultiTimeSummaries();
  }

  private toISODate(value: string | Date | null | undefined): string | null {
    if (!value) {return null;}
    if (typeof value === 'string') {return value;}
    if (value instanceof Date) {return LuxonDateTime.fromJSDate(value).toISODate();}
    return null;
  }

  onDateRangeChanged = (range: { start: string | Date; end: string | Date | null }) => {
    const startISO = this.toISODate(range.start);
    const endISO = this.toISODate(range.end);
    this.dateRangeStart = startISO;
    this.dateRangeEnd = endISO;
    this.form.patchValue({
      confirmedDate: startISO,
      rangeStartDate: range.start,
      rangeEndDate: range.end || range.start,
      timeType: 'confirmed'
    });
    const todayISO = LuxonDateTime.now().setZone(this.address.timezone).toISODate();
    this.showSameDayTimeAlert = this.dateRangeStart === todayISO;
    this.updateDateTimeSummary();
    this.fetchAcceptanceProbability();
    this.changeDetectorRef.markForCheck();
  };

  selectNoEarlierTime = (time: string) => {
    const rangeStartDate = this.form.value.rangeStartDate;
    const rangeEndDate = this.form.value.rangeEndDate;
    const noLaterTime = this.form.value.noLaterTime;
    if (rangeEndDate < rangeStartDate
        || (rangeEndDate <= rangeStartDate && noLaterTime && time > noLaterTime)) {
      this.form.patchValue({ noLaterTime: null });
      this.timeRangeEnd = null;
    }
    this.timeRangeStart = time;
    this.updateDateTimeSummary();
    this.fetchAcceptanceProbability();
    this.changeDetectorRef.markForCheck();
  };

  selectNoLaterTime = (time: string) => {
    const rangeStartDate = this.form.value.rangeStartDate;
    const rangeEndDate = this.form.value.rangeEndDate;
    const noEarlierTime = this.form.value.noEarlierTime;
    if (rangeEndDate < rangeStartDate
        || (rangeEndDate <= rangeStartDate && noEarlierTime && time < noEarlierTime)) {
      this.form.patchValue({ noEarlierTime: null });
      this.timeRangeStart = null;
    }
    this.timeRangeEnd = time;
    this.updateDateTimeSummary();
    this.fetchAcceptanceProbability();
    this.changeDetectorRef.markForCheck();
  };

  private updateDateTimeSummary() {
    const formatDate = (d: string) => LuxonDateTime.fromISO(d).toFormat('ccc LLL d');
    const formatTime = (t: string) => LuxonDateTime.fromFormat(t, 'HH:mm').toFormat('h:mma').toLowerCase();
    const parts: string[] = [];
    if (this.dateRangeStart) {
      parts.push(this.dateRangeEnd && this.dateRangeEnd !== this.dateRangeStart
        ? `${formatDate(this.dateRangeStart)} – ${formatDate(this.dateRangeEnd)}`
        : formatDate(this.dateRangeStart));
    }
    if (this.timeRangeStart) {
      parts.push(this.timeRangeEnd && this.timeRangeEnd !== this.timeRangeStart
        ? `${formatTime(this.timeRangeStart)} – ${formatTime(this.timeRangeEnd)}`
        : formatTime(this.timeRangeStart));
    }
    this.dateTimeSummary = parts.join(' · ') || '';
  }

  // --- Star rating filter ---

  changeStarRatingFilter = (event: any) => {
    const newFilter = event?.value ?? event;
    this.selectedStarRatingFilter = newFilter;
    this.form.patchValue({ proOption: newFilter });
    if (this.showSelectTime && this.currentStep === 4) {
      this.fetchAvailableTimes();
    }
  };
  
  private async fetchAcceptanceProbability() {
    this.didSelectAllRangeTimes = !!(this.dateRangeStart && this.timeRangeStart);
    if (!this.didSelectAllRangeTimes) {
      this.probabilityData = null;
      return;
    }
    try {
      this.isLoadingProbability = true;
      const bookData = {
        booking: {
          service_type_key: this.form.value.service,
          start_date_no_earlier_than: this.dateRangeStart,
          start_time_no_earlier_than: this.timeRangeStart,
          end_date_no_later_than: this.dateRangeEnd || this.dateRangeStart,
          end_time_no_later_than: this.timeRangeEnd || this.timeRangeStart
        }
      };
      this.probabilityData = await this.bookJobService.getRequestProbability(bookData, this.address.id);
    } catch (err) {
      this.probabilityData = null;
      this.logger.error(err);
    } finally {
      this.isLoadingProbability = false;
      this.changeDetectorRef.markForCheck();
    }
  }

  goToLearnMoreProbability() {
    this.util.openUrl('https://help.tidy.com/acceptance-probability');
  }

  // --- Select-time mode methods (when showSelectTime=true) ---

  async fetchAvailableTimes() {
    this.isLoadingTimes = true;
    this.noAvailableTimes = false;
    this.dateTimes = [];
    this.selectedTimeValue = null;
    this.selectedTimePros = [];
    this.selectedTimeDate = null;
    this.timeCardsCollapsed = false;
    this.allTimesSelected = [];
    this.multiTimeStep = 0;
    this.filteredDateTimesForStep = [];
    this.updateMultiTimeSummaries();
    this.form.patchValue({ time: '' });
    try {
      const frequency = this.frequencyValue || 'once';
      const request = this.buildSelectTimeRequestPayload(frequency, this.startDay, this.endDay);
      const times = this.selectedPrivatePro
        ? await this.availableTimesForRange.getAvailableTimesPrivatePro(request)
        : await this.availableTimesForRange.getAvailableTimes(request);
      await this.handleAvailableTimesResponse(times);
      if (this.selectedPrivatePro) {
        this.buildDateSelect();
      }
      if (this.isMultiTimeMode()) {
        this.filteredDateTimesForStep = this.dateTimes;
      }
    } catch (err) {
      this.errorMessage = err?.error?.message ?? err?.message;
      this.util.showError(this.errorMessage);
      this.logger.error(err);
    } finally {
      this.isLoadingTimes = false;
      this.changeDetectorRef.detectChanges();
    }
  }

  private async resolveStarRatingAndFetchTimes() {
    if (this.selectedProId !== 'new') {
      this.selectedStarRatingFilter = 'all';
      this.form.patchValue({ proOption: 'all' });
      await this.fetchAvailableTimes();
      return;
    }
    this.isLoadingTimes = true;
    this.changeDetectorRef.detectChanges();
    const frequency = this.frequencyValue || 'once';
    const filtersToTry = ['prioritized_v2:4_6', 'prioritized_v2:4_2', 'all'];
    try {
      for (const filter of filtersToTry) {
        const request = this.buildSelectTimeRequestPayload(frequency, this.startDay, this.endDay);
        request.filter = filter;
        const response = await this.availableTimesForRange.getAvailableTimes(request);
        if (response.available_times?.length > 0) {
          this.selectedStarRatingFilter = filter;
          this.form.patchValue({ proOption: filter });
          await this.handleAvailableTimesResponse(response);
          if (this.isMultiTimeMode()) {
            this.filteredDateTimesForStep = this.dateTimes;
          }
          this.isLoadingTimes = false;
          this.hasResolvedStarRatingFilter = true;
          this.changeDetectorRef.detectChanges();
          return;
        }
      }
    } catch (err) {
      this.errorMessage = err?.error?.message ?? err?.message;
      this.util.showError(this.errorMessage);
      this.logger.error(err);
    }
    this.selectedStarRatingFilter = 'all';
    this.form.patchValue({ proOption: 'all' });
    await this.fetchAvailableTimes();
    this.hasResolvedStarRatingFilter = true;
  }

  private buildSelectTimeRequestPayload(frequency: string, startDate: string, endDate: string) {
    let proFilter = this.selectedProId !== 'new'
      ? `homekeeper:${this.selectedProId}`
      : (this.selectedStarRatingFilter || 'all');
    if (proFilter === '4') {
      proFilter = 'all';
    }
    return {
      frequency,
      start_date: startDate,
      end_date: endDate,
      region_id: this.address.region_id,
      address_id: this.address.id,
      service_type_key: this.form.value.service,
      filter: proFilter
    };
  }

  private async handleAvailableTimesResponse(response: AvailableTimesModel) {
    this.noAvailableTimes = !response.available_times?.length;
    if (this.noAvailableTimes && this.hasSpecificPro() && !this.selectedPrivatePro) {
      try {
        this.proNotBookableReason = await this.homekeeper.getHkNotAvailableReason(this.selectedProId, this.form.value.service);
        this.minRateToBookWithPro = this.getMinRateToBookWithPro();
        this.notBookableHeader = this.computeNotBookableHeader();
      } catch (err) {
        this.proNotBookableReason = null;
        this.minRateToBookWithPro = null;
        this.notBookableHeader = '';
      }
    } else {
      this.proNotBookableReason = null;
      this.minRateToBookWithPro = null;
      this.notBookableHeader = '';
    }
    if (!this.noAvailableTimes) {
      this.dateTimes = response.available_times.map(at => ({
        date: at.date,
        times: this.formatAvailableTimes(at)
      }));
    }
  }

  private formatAvailableTimes(at: { date: string; times: any[] }) {
    if (this.selectedPrivatePro) {
      const proId = String(this.selectedProId);
      return at.times.map(t => {
        const timeStr = typeof t === 'string' ? t : t.time;
        const luxonDt = LuxonDateTime.fromISO(`${at.date}T${timeStr}`);
        return {
          value: `${at.date} ${timeStr}`,
          viewValue: luxonDt.toFormat('h:mma').toLowerCase(),
          pros: [proId]
        };
      });
    }
    return at.times.map(t => {
      const luxonDt = LuxonDateTime.fromISO(`${at.date}T${t.time}`);
      return {
        value: `${at.date} ${t.time}`,
        viewValue: luxonDt.toFormat('h:mma').toLowerCase(),
        pros: t.hk_ids
      };
    });
  }

  private buildDateSelect(): void {
    const parseDatePipe = new ParseDateTimePipe();
    this.privateJobDates = [{
      value: this.startDay,
      viewValue: parseDatePipe.transform(this.startDay, 'EEEE LLLL d')
    }];
    for (let i = 1; i <= 27; i++) {
      const luxonDate = LuxonDateTime.fromISO(this.startDay).plus({ days: i });
      const isoDate = luxonDate.toISODate();
      this.privateJobDates.push({
        value: isoDate,
        viewValue: parseDatePipe.transform(isoDate, 'EEEE LLLL d')
      });
    }
    this.form.patchValue({ privateDate: this.startDay });
  }

  changeDay = async (day: string) => {
    this.isLoadingPrivateTimes = true;
    this.noAvailableTimes = false;
    this.dateTimes = [];
    this.selectedTimeValue = null;
    this.selectedTimePros = [];
    this.selectedTimeDate = null;
    this.form.patchValue({ time: '' });
    this.changeDetectorRef.detectChanges();
    try {
      const frequency = this.frequencyValue || 'once';
      const request = this.buildSelectTimeRequestPayload(frequency, day, this.endDay);
      const times = await this.availableTimesForRange.getAvailableTimesPrivatePro(request);
      await this.handleAvailableTimesResponse(times);
    } catch (err) {
      this.errorMessage = err?.error?.message ?? err?.message;
      this.util.showError(this.errorMessage);
      this.logger.error(err);
    } finally {
      this.isLoadingPrivateTimes = false;
      this.changeDetectorRef.detectChanges();
    }
  };

  selectTime = (time: { value: string; viewValue: string; pros: number[] }) => {
    if (this.isMultiTimeMode()) {
      this.selectMultiTime(time);
      return;
    }
    this.selectedTimeValue = time.value;
    this.selectedTimePros = time.pros || [];
    const parts = time.value.split(' ');
    const dateStr = parts[0];
    const timeStr = parts[1];
    this.selectedTimeDate = dateStr;
    this.timeCardsCollapsed = true;
    this.form.patchValue({
      confirmedDate: dateStr,
      confirmedTime: timeStr,
      timeType: 'confirmed'
    });
    const formattedDate = LuxonDateTime.fromISO(dateStr).toFormat('ccc LLL d');
    this.dateTimeSummary = `${formattedDate  } at ${  time.viewValue}`;
    this.changeDetectorRef.detectChanges();
    if (this.currentStep === 4) {
      this.goNext();
    }
  };

  toggleTimeCards = () => {
    this.timeCardsCollapsed = !this.timeCardsCollapsed;
    this.changeDetectorRef.detectChanges();
  };

  // --- Multi-time selection (2x/3x per week) ---

  isMultiTimeMode(): boolean {
    return this.moreTimesPerWeekSelected && !this.showProRequestView;
  }

  private requiredTimeCount(): number {
    if (this.frequencyValue === 'thrice_a_week') {return 3;}
    if (this.frequencyValue === 'twice_a_week') {return 2;}
    return 1;
  }

  getMultiTimeLabel(): string {
    switch (this.multiTimeStep) {
      case 0: return 'Select First Time:';
      case 1: return 'Select Second Time:';
      case 2: return 'Select Third Time:';
      default: return '';
    }
  }

  getMultiTimeSummary(index: number): string {
    if (index >= this.allTimesSelected.length) {return '';}
    const time = this.allTimesSelected[index];
    const dateStr = time.value.split(' ')[0];
    const formattedDate = LuxonDateTime.fromISO(dateStr).toFormat('cccc LLL d');
    return `${formattedDate  } at ${  time.viewValue}`;
  }

  private updateMultiTimeSummaries(): void {
    this.multiTimeSummaryTexts = this.allTimesSelected.map((_, i) => this.getMultiTimeSummary(i));
  }

  getMultiTimeOrdinalLabel(index: number): string {
    switch (index) {
      case 0: return 'First Time: ';
      case 1: return 'Second Time: ';
      case 2: return 'Third Time: ';
      default: return '';
    }
  }

  editMultiTime = (index: number) => {
    this.allTimesSelected = this.allTimesSelected.slice(0, index);
    this.multiTimeStep = index;
    this.selectedTimeValue = null;
    this.selectedTimePros = [];
    this.selectedTimeDate = null;
    this.timeCardsCollapsed = false;
    this.updateMultiTimeSummaries();
    this.updateFilteredDateTimesForMultiStep();
    this.changeDetectorRef.detectChanges();
  };

  private updateFilteredDateTimesForMultiStep(): void {
    if (this.multiTimeStep === 0) {
      this.filteredDateTimesForStep = this.dateTimes;
      return;
    }
    const previousTime = this.allTimesSelected[this.multiTimeStep - 1];
    const previousDate = previousTime.value.split(' ')[0];
    const previousDateIndex = this.dateTimes.findIndex(dt => dt.date === previousDate);
    this.filteredDateTimesForStep = this.dateTimes.slice(previousDateIndex + 1, previousDateIndex + 7);
  }

  private selectMultiTime(time: { value: string; viewValue: string; pros: number[] }): void {
    this.allTimesSelected[this.multiTimeStep] = time;
    this.multiTimeStep++;
    this.updateMultiTimeSummaries();

    if (this.multiTimeStep >= this.requiredTimeCount()) {
      const firstTime = this.allTimesSelected[0];
      const parts = firstTime.value.split(' ');
      this.selectedTimeValue = firstTime.value;
      this.selectedTimeDate = parts[0];
      this.form.patchValue({
        confirmedDate: parts[0],
        confirmedTime: parts[1],
        timeType: 'confirmed'
      });
      this.updateMultiTimeDateTimeSummary();
      this.timeCardsCollapsed = true;
      this.changeDetectorRef.detectChanges();
      if (this.currentStep === 4) {
        this.goNext();
      }
    } else {
      this.timeCardsCollapsed = false;
      this.updateFilteredDateTimesForMultiStep();
      this.changeDetectorRef.detectChanges();
    }
  }

  private updateMultiTimeDateTimeSummary(): void {
    const summaries = this.allTimesSelected.map((_, index) => this.getMultiTimeSummary(index));
    this.dateTimeSummary = summaries.join(' + ');
  }

  // --- Raise rates to book ---

  private getMinRateToBookWithPro(): number | null {
    if (this.proNotBookableReason?.not_bookable_reason?.includes('rate is set at a minimum')) {
      const regex = /minimum of \$([\d,]+(?:\.\d{1,2})?)/;
      const match = this.proNotBookableReason.not_bookable_reason.match(regex);
      return match ? parseFloat(match[1].replace(/,/g, '')) * 100 : null;
    }
    return null;
  }

  private computeNotBookableHeader(): string {
    if (this.proNotBookableReason?.text?.includes('completely booked')) {
      return 'Fully Booked';
    } else if (this.minRateToBookWithPro) {
      return 'Raise Rates to Book';
    }
    return 'No Times Available';
  }

  changeServiceRates = async () => {
    try {
      const currentPrices = await this.addressesService.getPriceTable(this.address.id);
      const payload = { new_prices: [] };
      currentPrices.map((service) => {
        if (service.key === this.form.value.service) {
          payload.new_prices.push({
            no_team_address_service_id: service.id,
            price: this.minRateToBookWithPro
          });
        } else if (service.billing_type !== 'price_later') {
          payload.new_prices.push({
            no_team_address_service_id: service.id,
            price: service.prices.base_price
          });
        }
      });
      await this.addressesService.changePrices(this.address.id, payload);
      await this.fetchAvailableTimes();
      this.util.showSuccess('Rates updated!');
    } catch (err) {
      this.errorMessage = err?.error?.message ?? err?.message;
      this.util.showError(this.errorMessage);
    }
  };

  viewAllAvailability = async () => {
    this.selectedProId = 'new';
    this.selectedProName = '';
    this.selectedPrivatePro = false;
    this.form.patchValue({ proSelect: 'new' });

    // Re-fetch services for "Find New Pros" to check if current service is still available
    this.invalidatePrefetch();
    const categoryKey = this.selectedSubcategory?.apiKey || 'regular_cleaning';
    await this.prefetchStep2Data(this.categoryId, categoryKey);

    if (this.choseService && this.choseFrequency) {
      // Service + frequency were re-selected by prefetchStep2Data (service exists for new pros)
      await this.fetchAvailableTimes();
    } else {
      // Service not available for "Find New Pros" — go back to Details step
      this.currentStep = 3;
      this.highestStepReached = Math.max(this.highestStepReached, 3);
    }
    this.changeDetectorRef.detectChanges();
  };

  // --- Pro request mode (checkbox day + time range UI) ---

  hasSpecificPro(): boolean {
    return this.selectedProId !== 'new';
  }

  isProRequestMode(): boolean {
    return this.selectedProId !== 'new' && this.showProRequestView;
  }

  switchToProRequestView = () => {
    this.showProRequestView = true;
    if (this.proRequestDays.length === 0) {
      this.initProRequestDays();
    }
    this.changeDetectorRef.detectChanges();
  };

  switchToAvailableTimes = () => {
    this.showProRequestView = false;
    this.changeDetectorRef.detectChanges();
  };

  switchToRequestTimeView = () => {
    this.showRequestTimeView = true;
    this.selectedTimeValue = null;
    this.selectedTimePros = [];
    this.selectedTimeDate = null;
    this.timeCardsCollapsed = false;
    // The request-time form (Earliest Start / Latest Finish dropdowns) uses
    // proRequestTimeOpts for its time selects. Populate it here so the
    // dropdowns aren't empty when this view first opens — initProRequestDays
    // was previously only called from switchToProRequestView.
    if (this.proRequestTimeOpts.length === 0) {
      this.loadProRequestTimeOpts();
    }
    this.changeDetectorRef.detectChanges();
  };

  switchToConfirmedTimes = () => {
    this.showRequestTimeView = false;
    this.dateRangeStart = null;
    this.dateRangeEnd = null;
    this.timeRangeStart = null;
    this.timeRangeEnd = null;
    this.form.patchValue({ noEarlierTime: null, noLaterTime: null });
    this.dateTimeSummary = '';
    this.changeDetectorRef.detectChanges();
  };

  initProRequestDays() {
    this.proRequestDays = this.client.requestableDates();
    this.proRequestWeekDayCheckBox = Array(28).fill(null).map(() => this.fb.group({ checked: false }));
    this.proRequestTimeRangeForms = Array(28).fill(null).map(() => []);
    this.proRequestSelectedTimes = [];
    this.proRequestNotSelectedTimesError = false;
    this.loadProRequestTimeOpts();
    this.updateProRequestEndTimeOptions();
  }

  private loadProRequestTimeOpts() {
    if (this.selectedPrivatePro) {
      this.proRequestTimeOpts = this.timeRanges.timeRangesOpts();
    } else {
      this.proRequestTimeOpts = this.timeRanges.tidyJobRangesOpts();
    }
    const currentTime = LuxonDateTime.fromObject({}, { zone: this.address.timezone });
    const _30minAhead = currentTime.plus({ minutes: 30 }).toFormat('HH:mm');
    this.proRequestTodayStartOpts = this.proRequestTimeOpts.filter(opt => opt.value > _30minAhead);
  }

  proRequestMarkCheckbox = (value: boolean, dayIndex: number) => {
    if (value) {
      this.proRequestAddNewTimeRange(dayIndex);
    } else {
      this.proRequestTimeRangeForms[dayIndex] = [];
    }
    this.proRequestUpdateValuesValidity();
    this.updateProRequestEndTimeOptions();
  };

  proRequestAddNewTimeRange = (dayIndex: number) => {
    if (!this.proRequestTimeRangeForms[dayIndex]) {
      this.proRequestTimeRangeForms[dayIndex] = [];
    }
    const weekDayForms = this.proRequestTimeRangeForms[dayIndex];
    const duration = this.selectedServiceData?.duration || 30;
    const startOpts = dayIndex === 0 ? this.proRequestTodayStartOpts : this.proRequestTimeOpts;

    let newStartTime: string;
    if (weekDayForms.length > 0) {
      const lastEndTime = weekDayForms[weekDayForms.length - 1].value.end_time;
      const nextOpt = startOpts.find(opt => opt.value > lastEndTime);
      if (!nextOpt) {return;}
      newStartTime = nextOpt.value;
    } else {
      newStartTime = startOpts[0]?.value || '08:00';
    }

    const endOpts = this.proRequestEndTimes(newStartTime);
    if (endOpts.length === 0) {return;}
    const newEndTime = endOpts[0].value;

    this.timeRanges.addTimeRange(newStartTime, newEndTime, weekDayForms, 0);
    this.proRequestUpdateValuesValidity();
    this.updateProRequestEndTimeOptions();
  };

  proRequestRemoveTimeRange = (dayIndex: number, timeRangeIndex: number) => {
    this.proRequestTimeRangeForms[dayIndex].splice(timeRangeIndex, 1);
    this.proRequestUpdateValuesValidity();
    this.updateProRequestEndTimeOptions();
  };

  proRequestUpdateValuesValidity() {
    this.proRequestSelectedTimes = this.proRequestParseRangesForms();
    this.proRequestTimeRangeForms.forEach((dayForms, index) => {
      this.proRequestWeekDayCheckBox[index]?.patchValue({ checked: dayForms.length > 0 });
    });
    this.updateDateTimeSummaryForProRequest();
  }

  private updateDateTimeSummaryForProRequest() {
    const count = this.proRequestSelectedTimes.length;
    if (count > 0) {
      this.dateTimeSummary = count === 1 ? '1 day requested' : `${count} days requested`;
    } else {
      this.dateTimeSummary = '';
    }
  }

  proRequestParseRangesForms(): any[] {
    return this.proRequestTimeRangeForms.map((fgArray, index) => {
      const times = fgArray.map(formGroup => formGroup.value);
      if (times?.length) {
        return {
          date: this.proRequestDays[index].date,
          times
        };
      }
    }).filter(elem => elem);
  }

  proRequestValidForms(): boolean {
    return this.proRequestTimeRangeForms.every(fgArray => fgArray.every(formGroup => formGroup.valid));
  }

  proRequestEndTimes = (startTime: string) => {
    if (!startTime) {return [];}
    if (this.selectedPrivatePro) {
      return this.proRequestTimeOpts.filter(opt => opt.value > startTime);
    }
    const duration = this.selectedServiceData?.duration || 30;
    const serviceEnd = LuxonDateTime.fromFormat(startTime, 'HH:mm')
      .plus({ minutes: duration })
      .toFormat('HH:mm');
    return this.proRequestTimeOpts.filter(opt => opt.value >= serviceEnd);
  };

  private updateProRequestEndTimeOptions(): void {
    this.proRequestEndTimeOptions = this.proRequestTimeRangeForms.map(dayForms =>
      dayForms.map(formGrp => this.proRequestEndTimes(formGrp.value.start_time))
    );
  }

  proRequestStartTimeChange = (formGrp: UntypedFormGroup) => {
    const start = formGrp.value.start_time;
    const duration = this.selectedServiceData?.duration || 30;
    const nextEnd = LuxonDateTime.fromFormat(start, 'HH:mm')
      .plus({ minutes: duration })
      .toFormat('HH:mm');
    formGrp.patchValue({ end_time: nextEnd });
    this.updateProRequestEndTimeOptions();
  };

  // =============================================
  // Step 4: Confirm/Payment (ported from confirm-booking-payment.ts)
  // =============================================

  async prepareStep3Data() {
    try {
      this.viewData = this.buildViewData();
      this.bookData = this.buildBookData();

      if (this.bookingType !== 'add_one_time_job') {
        this.priceExperiment = this.bookJobService.getPriceExperiment();
        this.subscriptionExperiment = this.bookJobService.getSubscriptionExperiment();
      }
      this.currentSubscription = await this.storage.retrieve('currentSubscription');

      this.giftCode = false;
      this.giftCodeForm = false;
      const giftCodeParam = this.cookieService.get('giftCodeParam');
      const appliedGiftCode = this.getAppliedGiftCodeFromStorage();
      try {
        if (giftCodeParam) {
          this.giftCode = await this.client.validateGiftCode(giftCodeParam);
          this.giftCodeForm = true;
          this.giftForm.patchValue({ giftCode: this.giftCode.code });
        } else if (appliedGiftCode) {
          this.giftCodeForm = true;
          this.giftForm.patchValue({ giftCode: appliedGiftCode.code });
        }
      } catch (_err) {
        // Gift code validation failed, continue
      }

      this.alreadyHasChargeAfterSetting = await this.storage.retrieve('billingType') === 'after_cleaning';
      if (this.alreadyHasChargeAfterSetting) {
        this.chargeBeforePrice = Math.round(this.viewData.price * .909090901 / 100) * 100;
      } else {
        this.chargeBeforePrice = this.viewData.price;
      }
      if (this.viewData?.frequency === 'One Time (+25%)') {
        this.chargeBeforePrice = this.bookJobService.getOneTimePrice(this.chargeBeforePrice);
      }
      if (!this.form.value.chargeType) {
        this.form.patchValue({ chargeType: this.alreadyHasChargeAfterSetting ? 'after_cleaning' : 'before_cleaning' });
      }
      if (this.form.value.chargeType === 'after_cleaning') {
        this.viewData.price = Math.round((this.chargeBeforePrice + this.chargeBeforePrice * .1) / 100) * 100;
      } else {
        this.viewData.price = this.chargeBeforePrice;
      }

      const userRole = localStorage.getItem('user_role');
      if (userRole !== 'member') {
        const creditBalance = parseInt(localStorage.getItem('creditBalance'));
        if (creditBalance) {
          this.creditBalance = creditBalance;
          this.employerName = localStorage.getItem('employerName');
        } else {
          const billingData = this.billingDataPromise
            ? await this.billingDataPromise
            : await this.client.getBillingData();
          this.creditBalance = billingData?.balance;
          this.employerName = billingData?.balance_details[0]?.transferred_from?.account_name;
        }
      }

      this.displayPrice = this.getDisplayPrice();
      this.priceBodyText = this.displayPrice?.trim() === 'Priced later'
        ? 'This type of job is typically priced after. We\'ll find the best pro and you can discuss pricing with them.'
        : this.displayPrice;
      const categoryNameLower = this.selectedCategory?.viewValue?.toLowerCase() || '';
      this.satisfactionGuaranteeText = this.isHouseCleaning
        ? 'Unhappy with your first cleaning? Get a new one free.'
        : `Unhappy with your first ${  categoryNameLower  }? Get a new one free.`;
      this.customizableText = this.isHouseCleaning
        ? 'Your cleaning, your way. Include anything you like.'
        : `Your ${  categoryNameLower  }, your way. Include anything you like.`;
      this.updatePaymentOptions();
      this.isZeroCostService = this.getPayNowPrice() === 0;

      const todayISO = LuxonDateTime.now().setZone(this.address.timezone).toISODate();
      const bookDates = this.bookData?.dates?.map(d => d.date) || [];
      if (this.dateRangeStart) {bookDates.push(this.dateRangeStart);}
      if (this.proRequestSelectedTimes?.length) {
        this.proRequestSelectedTimes.forEach(t => bookDates.push(t.date));
      }
      this.showSameDayTimeAlert = bookDates.some(d => d === todayISO);
      if (this.showSameDayTimeAlert) {
        if (this.customFlowName) {
          this.sameDayPrice = this.bookJobService.getCustomDiscountedPrice(this.viewData.price * 1.5);
        } else {
          this.sameDayPrice = Math.round(this.viewData.basePrice * 1.5 / 100) * 100;
        }
      }

      this.updateButtonTitle();

      if (this.customFlowName) {
        this.setRequiredCustomFlowFields();
      }

      this.changeDetectorRef.detectChanges();

      // Mount Stripe card element after template renders
      setTimeout(() => this.initiateCardElement(), 500);
    } catch (err) {
      this.errorMessage = err?.error?.message ?? err?.message;
      this.util.showError(this.errorMessage);
      this.logger.error(err);
    }
  }

  buildViewData() {
    const timeType = this.form.value.timeType;
    let dateTimeLabel = 'Quote requested';
    if (this.isProRequestMode()) {
      dateTimeLabel = this.dateTimeSummary || 'Select times';
    } else if (this.isRangeSelection()) {
      dateTimeLabel = this.dateTimeSummary || 'Time range selected';
    } else if (timeType === 'confirmed') {
      if (this.isMultiTimeMode() && this.allTimesSelected.length > 0) {
        dateTimeLabel = this.getMultiTimeSummary(0);
      } else {
        dateTimeLabel = this.form.value.confirmedDate || 'Date selected';
      }
    }
    const data: any = {
      service: this.selectedService,
      frequency: this.selectedFrequency,
      image: this.selectedServiceImage,
      dateTime: dateTimeLabel,
      price: this.selectedPrice || this.selectedServiceData?.price,
      duration: this.selectedDuration,
      billingType: this.selectedBillingType,
      basePrice: this.selectedServiceData?.price
    };
    if (this.isMultiTimeMode() && this.allTimesSelected.length >= 2) {
      data.dateTimeTwo = this.getMultiTimeSummary(1);
    }
    if (this.isMultiTimeMode() && this.allTimesSelected.length >= 3) {
      data.dateTimeThree = this.getMultiTimeSummary(2);
    }
    return data;
  }

  buildBookData() {
    const timeType = this.form.value.timeType;
    const frequency = this.frequencyValue || 'once';

    if (timeType === 'quote') {
      return {
        address_id: this.address.id,
        service_type_key: this.form.value.service,
        frequency,
        is_quote: true
      };
    }

    const confirmedDate = this.form.value.confirmedDate;
    const confirmedTime = this.form.value.confirmedTime || '09:00';
    let dates: Array<{ date: string; time: string; homekeeper_ids: any[] }>;
    if (this.isMultiTimeMode() && this.allTimesSelected.length > 0) {
      dates = this.allTimesSelected.map(t => ({
        date: t.value.split(' ')[0],
        time: t.value.split(' ')[1],
        homekeeper_ids: t.pros || []
      }));
    } else {
      dates = [{
        date: confirmedDate || this.startDay,
        time: confirmedTime,
        homekeeper_ids: this.selectedTimePros
      }];
    }

    const config = (this.configuration?.title !== 'Reschedule' && frequency === 'once')
      ? bookJobConfigurations['add_one_time_job']
      : this.configuration;

    return config.buildBookingData({
      addressId: this.address.id,
      frequency,
      planId: this.planId,
      serviceTypeKey: this.form.value.service,
      dates,
      ratingChosen: false,
      hkRating: this.bookJobService.parseFilterToRating(this.form.value.proOption)
    });
  }

  private isRangeSelection(): boolean {
    if (this.showRequestTimeView) {return true;}
    if (this.showSelectTime || this.hasSpecificPro()) {return false;}
    const hasDateRange = !!this.dateRangeEnd && this.dateRangeEnd !== this.dateRangeStart;
    const hasTimeRange = !!this.timeRangeEnd && this.timeRangeEnd !== this.timeRangeStart;
    return hasDateRange || hasTimeRange;
  }

  private buildRequestData(): any {
    return {
      address_id: this.address.id,
      booking: {
        service_type_key: this.form.value.service,
        start_date_no_earlier_than: this.dateRangeStart || this.startDay,
        start_time_no_earlier_than: this.timeRangeStart || '08:00',
        end_date_no_later_than: this.dateRangeEnd || this.dateRangeStart || this.startDay,
        end_time_no_later_than: this.timeRangeEnd || this.timeRangeStart || '18:30',
        request_type: 'waterfall'
      },
      rating_options: {
        hk_rating: this.bookJobService.parseFilterToRating(this.form.value.proOption),
        rating_chosen: false
      }
    };
  }

  initiateCardElement(): void {
    if (this.cardEl) {
      this.cardEl.removeEventListener('change', this.cardHandler);
      this.cardEl.destroy();
      this.cardEl = null;
    }
    if (!this.cardInfo?.nativeElement) {
      return;
    }
    setTimeout(() => {
      this.cardEl = this.stripeProvider.getElements().create('card', getCardOptions(!this.themeService.isDarkMode()));
      this.cardEl.mount(this.cardInfo.nativeElement);
      this.cardEl.addEventListener('change', this.cardHandler);
    }, 500);
  }

  onChange({ error }) {
    this.errorMessage = error ? error.message : null;
    this.changeDetectorRef.detectChanges();
  }

  getDisplayPrice(): string {
    const giftCode = this.giftCode?.amount || 0;
    const subscriptionAmount = this.isUpdatingSubscription ? this.currentSubscription?.amount || 0 : 0;
    return this.bookJobService.getDisplayPriceForJob(
      this.planId, this.selectedServiceData, this.viewData?.frequency,
      true, this.bookingType, this.hasNonTrialPaidSubscription,
      this.priceExperiment, this.bookingExperiment, this.customFlowName,
      this.selectedPrivatePro, giftCode, subscriptionAmount
    );
  }

  setRequiredCustomFlowFields() {
    this.form.controls.firstName.setValidators([Validators.required, Validators.minLength(2)]);
    this.form.controls.lastName.setValidators([Validators.required, Validators.minLength(2)]);
    this.form.controls.email.setValidators([Validators.required, validationUtils.validateEmail]);
    this.form.controls.phone.setValidators([Validators.required]);
  }

  getPayNowPrice(): number {
    return this.chargeBeforePrice || 0;
  }

  getPayAfterPrice(): number {
    return Math.round((this.chargeBeforePrice + this.chargeBeforePrice * .1) / 100) * 100 || 0;
  }

  getSavingsAmount(): number {
    return this.getPayAfterPrice() - this.getPayNowPrice();
  }

  updatePaymentOptions(): void {
    const formatCurrency = (cents: number) => `$${(cents / 100).toFixed(2).replace(/\.00$/, '')}`;
    this.paymentOptions = [
      {
        viewValue: `Pay Now - ${formatCurrency(this.getPayNowPrice())}`,
        value: 'before_cleaning',
        savingsTag: `Save ${formatCurrency(this.getSavingsAmount())}`,
        extraText: 'Fully refundable until 24 hours before the job'
      },
      {
        viewValue: `Pay After Job - ${formatCurrency(this.getPayAfterPrice())}`,
        value: 'after_cleaning'
      }
    ];
  }

  updateButtonTitle() {
    if (this.isProRequestMode() || this.isRangeSelection()) {
      this.buttonTitle = 'Request Now';
    } else {
      this.buttonTitle = 'Book Now';
    }
  }

  changePrice = (chargeType) => {
    if (chargeType.value === 'before_cleaning') {
      this.viewData.price = this.chargeBeforePrice;
    } else {
      this.viewData.price = Math.round((this.chargeBeforePrice + this.chargeBeforePrice * .1) / 100) * 100;
    }
    this.selectedServiceData.price = this.viewData.price;
    this.displayPrice = this.getDisplayPrice();
    this.updateButtonTitle();
    if (this.showSameDayTimeAlert) {
      if (this.customFlowName) {
        this.sameDayPrice = this.bookJobService.getCustomDiscountedPrice(this.viewData.price * 1.5);
      } else {
        this.sameDayPrice = Math.round(this.viewData.price * 1.5 / 100) * 100;
      }
    }
  };

  @Loading('', true)
  async submitGiftCode() {
    this.giftCodeErrorMessage = null;
    this.giftCode = false;
    this.displayPrice = this.getDisplayPrice();
    this.updateButtonTitle();
    if (this.giftForm.value.giftCode === '' || this.giftForm.value.giftCode === null) {
      return;
    }
    try {
      const referralCode = await this.client.validateReferralCode(this.giftForm.value.giftCode);
      if (referralCode.valid) {
        this.giftCode = true;
        this.referralCode = true;
      } else {
        this.giftCode = await this.client.validateGiftCode(this.giftForm.value.giftCode);
        this.form.patchValue({ giftCode: this.giftForm.value.giftCode });
      }
      this.displayPrice = this.getDisplayPrice();
      this.updatePaymentOptions();
      this.updateButtonTitle();
    } catch (err) {
      this.giftCodeErrorMessage = err?.error?.message ?? err?.message;
    }
  }

  @Loading('', true)
  async confirmBooking() {
    this.errorMessage = '';
    this.submitted = true;
    this.termsError = false;
    if (!this.termsChecked) {
      this.termsError = true;
      return;
    }
    this.storage.delete('addresses');

    if (!this.hasValidCC) {
      const { token, error } = await this.stripeProvider.getInstance().createToken(this.cardEl);
      if (error) {
        this.errorMessage = 'Your card was declined again. Please try another card.';
        return;
      }
      try {
        if (this.isUpdatingSubscription && this.currentSubscription) {
          const payload = { plan_type_key: this.currentSubscription.key };
          await this.me.changeSubscription(payload);
        }
        if (this.customFlowName) {
          const payload = {
            profile: {
              last_name: this.form.value.lastName.trim(),
              first_name: this.form.value.firstName.trim(),
              email: this.form.value.email,
              phone: parsePhoneNumber(this.countryCode + this.form.value.phone)
            }
          };
          await this.client.saveClientSettings(payload);
        }
        if (this.form.value.chargeType === 'after_cleaning' || (this.form.value.chargeType === 'before_cleaning' && this.alreadyHasChargeAfterSetting)) {
          const chargeSettings: any = { billing: { charge: { type: this.form.value.chargeType } } };
          await this.client.saveClientSettings(chargeSettings);
        }
        await this.card.saveCard({
          type: 'card',
          stripe_card_token: token.id,
          primary_payment_method: true,
        });
        localStorage.setItem('hasCard', 'true');
        await this.submitBooking();
      } catch (err) {
        if (err.code === 402) {
          this.errorMessage = 'Your card was declined again. Please try another card.';
        } else {
          this.errorMessage = err?.error?.message ?? err?.message;
        }
      }
    } else {
      try {
        if (this.form.value.chargeType === 'after_cleaning' || (this.form.value.chargeType === 'before_cleaning' && this.alreadyHasChargeAfterSetting)) {
          const chargeSettings: any = { billing: { charge: { type: this.form.value.chargeType } } };
          await this.client.saveClientSettings(chargeSettings);
        }
        await this.submitBooking();
      } catch (err) {
        this.errorMessage = err?.error?.message ?? err?.message;
      }
    }
  }

  private async submitBooking() {
    if (this.giftCode) {
      if (this.referralCode) {
        this.bookData['referral_code'] = this.giftForm.value.giftCode;
      } else {
        await this.client.redeemGiftCode(this.giftForm.value.giftCode);
      }
    }
    if (this.customFlowName) {
      const code = localStorage.getItem('customFlowReferralCode');
      if (code) {
        await this.client.redeemGiftCode(code);
      }
    }

    let submissionType: string;
    if (this.isProRequestMode()) {
      await this.submitAsProRequest();
      submissionType = 'pro_request';
    } else if (this.isRangeSelection()) {
      const bookedDirectly = this.selectedProId === 'new' && await this.tryBookFirstAvailableTime();
      if (bookedDirectly) {
        submissionType = 'range_to_booking';
      } else {
        await this.submitAsRequest();
        submissionType = 'request';
      }
    } else {
      await this.submitAsBooking();
      submissionType = 'booking';
    }

    if (!this.isReschedule()) {
      this.posthogService.capture('booking_completed', {
        variant: this.pageVariant,
        has_credit_card: this.hasValidCC,
        booking_type: this.bookingType,
        submission_type: submissionType,
        service_type: this.form?.value?.service || null,
        service_type_key: this.form?.value?.service || null,
        category: this.showNonCleaningServices ? (this.selectedSubcategory?.name || 'House Cleaning') : 'House Cleaning',
        is_house_cleaning: this.isHouseCleaning,
        frequency: this.frequencyValue || 'once',
        is_desktop: this.windowService.isDesktopRes,
      });
    }

    localStorage.setItem('didBookJob', 'true');
    localStorage.removeItem('customFlowReferralCode');
    localStorage.removeItem('customFlowSmallImage');
    localStorage.removeItem('appliedGiftCode');
    localStorage.setItem('isUpdatingSubscription', 'false');

    await this.storage.save('isFirstBooking', false);
    await this.storage.save('cartMetadata', null);
  }

  private async submitAsBooking() {
    const bookingFormData = this.dynamicBookingForm?.isReady
      ? this.dynamicBookingForm.buildBookingFormData()
      : this.cachedBookingFormData;
    if (bookingFormData?.input_answers?.length > 0) {
      this.bookData['booking_form'] = bookingFormData;
    }

    const isBookingOneTimePlan = this.bookingType === 'add_job' && !this.bookData.frequency;
    if (isBookingOneTimePlan) {
      this.bookingType = 'add_one_time_job';
    }
    await this.bookJobService.saveJob(this.bookingType, this.bookData, this.planId, this.bookingId);
    this.navigateAfterSuccess(false);
  }

  private async tryBookFirstAvailableTime(): Promise<boolean> {
    try {
      const frequency = this.frequencyValue || 'once';
      const request = {
        frequency,
        start_date: this.dateRangeStart || this.startDay,
        end_date: this.dateRangeEnd || this.dateRangeStart || this.startDay,
        start_time: this.timeRangeStart || '08:00',
        end_time: this.timeRangeEnd || this.timeRangeStart || '18:30',
        region_id: this.address.region_id,
        address_id: this.address.id,
        service_type_key: this.form.value.service,
        filter: (this.selectedStarRatingFilter === '4' ? 'all' : this.selectedStarRatingFilter) || 'all'
      };
      const response = await this.availableTimesForRange.getAvailableTimes(request);
      if (response?.available_times?.length > 0 && response.available_times[0].times?.length > 0) {
        const firstDay = response.available_times[0];
        const firstTime = firstDay.times[0];
        this.selectedTimePros = firstTime.hk_ids || [];
        this.form.patchValue({
          confirmedDate: firstDay.date,
          confirmedTime: firstTime.time,
          timeType: 'confirmed'
        });
        this.bookData = this.buildBookData();
        await this.submitAsBooking();
        return true;
      }
      return false;
    } catch (err) {
      this.logger.error(err, 'tryBookFirstAvailableTime');
      return false;
    }
  }

  private async submitAsRequest() {
    const requestData = this.buildRequestData();

    const bookingFormData = this.dynamicBookingForm?.isReady
      ? this.dynamicBookingForm.buildBookingFormData()
      : this.cachedBookingFormData;
    if (bookingFormData?.input_answers?.length > 0) {
      requestData['booking_form'] = bookingFormData;
    }

    if (this.isRescheduleOfOneJob()) {
      await createThenCancelOriginal({
        create: () => this.requests.addJobRequest(this.address.id, requestData),
        cancelOriginal: () => this.schedule.cancelJob(this.bookingId),
        undoCreate: (created) => this.cancelCreatedBooking(created),
      });
    } else {
      await this.cancelPlanBeingRescheduled();
      await this.requests.addJobRequest(this.address.id, requestData);
    }
    this.navigateAfterSuccess(true);
  }

  private async submitAsProRequest() {
    const requestTimeBlocks = this.proRequestParseRangesForms();
    const payload: any = {
      address_id: this.address.id,
      homekeeper_id: this.selectedProId,
      service_type_key: this.form.value.service,
      frequency: this.frequencyValue || 'once',
      request_time_blocks: requestTimeBlocks
    };

    const bookingFormData = this.dynamicBookingForm?.isReady
      ? this.dynamicBookingForm.buildBookingFormData()
      : this.cachedBookingFormData;
    if (bookingFormData?.input_answers?.length > 0) {
      payload['booking_form'] = bookingFormData;
    }

    if (this.isRescheduleOfOneJob()) {
      await createThenCancelOriginal({
        create: () => this.requests.addProRequest(this.address.id, payload),
        cancelOriginal: () => this.schedule.cancelJob(this.bookingId),
      });
    } else {
      await this.cancelPlanBeingRescheduled();
      await this.requests.addProRequest(this.address.id, payload);
    }
    this.navigateAfterSuccess(true);
  }

  private isRescheduleOfOneJob(): boolean {
    return this.isReschedule() && this.bookingType === 'reschedule_job' && !!this.bookingId;
  }

  private async cancelPlanBeingRescheduled() {
    if (this.isReschedule() && this.bookingType === 'reschedule_plan' && this.planId) {
      await this.schedule.cancelPlan(this.planId);
    }
  }

  private async cancelCreatedBooking(created: any) {
    const newBookingId = createdBookingId(created);
    if (!newBookingId) {
      throw new Error('No booking to cancel');
    }
    await this.schedule.cancelJob(newBookingId);
  }

  private navigateAfterSuccess(isJobRequest: boolean) {
    const params = {
      isNormalBookingFlow: true,
      viewData: this.viewData,
      isJobRequest
    };
    const userRole = localStorage.getItem('user_role');
    if (userRole === 'member') {
      const shouldShowAllToDosPage = localStorage.getItem('shouldShowAllToDosPage') === 'true';
      this.navCtrl.navigateForward(shouldShowAllToDosPage || this.windowService.isDesktopRes ? 'schedule' : 'schedule-list');
    } else if (this.hasValidCC) {
      if (this.isRightSideContent) {
        this.rightSidePanelService.closeRightSidePanel();
        this.util.showSuccess('Successfully booked your job!');
      } else {
        this.navCtrl.navigateForward('schedule');
      }
    } else {
      localStorage.setItem('madeUpdateToSchedule', 'true');
      this.rightSidePanelService.navigateTo('property-options', params, PropertyOptionsPage);
    }
  }

  openTerms = () => {
    this.util.openUrl('https://www.tidy.com/terms');
  };

  onCountryCodeChange(countryCode: string) {
    this.countryCode = countryCode.replace('+', '');
  }

  private getAppliedGiftCodeFromStorage(): { code: string } | null {
    try {
      const stored = localStorage.getItem('appliedGiftCode');
      if (stored) {
        return JSON.parse(stored);
      }
    } catch {
      // Ignore parse errors
    }
    return null;
  }

  private saveBookingFormState(): void {
    if (!this.dynamicBookingForm || !this.categoryId) {return;}
    this.savedFormStateByCategory.set(this.categoryId, {
      formValues: { ...this.dynamicBookingForm.getFormValue() },
      attachments: [...this.dynamicBookingForm.attachments],
      checkboxes: this.dynamicBookingForm.selectedCheckboxes.map(c => ({
        ...c,
        value: [...c.value],
      })),
    });
  }

  private restoreBookingFormState(): void {
    const saved = this.savedFormStateByCategory.get(this.categoryId);
    if (!saved || !this.dynamicBookingForm) {return;}
    this.dynamicBookingForm.form.patchValue(saved.formValues);
    this.dynamicBookingForm.attachments = saved.attachments || [];
    this.dynamicBookingForm.selectedCheckboxes = saved.checkboxes || [];

    // Restore requiresTextArea flags for select fields
    if (this.bookingForms?.[0]?.inputs) {
      for (const field of this.bookingForms[0].inputs) {
        if (field.type === 'select') {
          const controlValue = saved.formValues[field.id?.toString()];
          if (controlValue) {
            const selectedItem = field.items?.find(i => i.value === controlValue);
            field.requiresTextArea = !selectedItem?.responseValue;
          }
        }
      }
    }
    this.changeDetectorRef.detectChanges();
  }

  private buildDetailsSummaryItems(): { label: string; value?: string; imageUrls?: string[] }[] {
    const items: { label: string; value?: string; imageUrls?: string[] }[] = [];
    if (!this.bookingForms?.[0]?.inputs || !this.dynamicBookingForm) {
      return items;
    }

    const formValue = this.dynamicBookingForm.getFormValue();
    const attachments = this.dynamicBookingForm.attachments;
    const selectedCheckboxes = this.dynamicBookingForm.selectedCheckboxes;

    for (const field of this.bookingForms[0].inputs) {
      const controlValue = formValue?.[field.id?.toString()];

      if (field.data?.type === 'image' || field.data?.type === 'images') {
        const attachment = attachments.find(a => a.input_id === field.id);
        if (attachment?.value) {
          const urls = Array.isArray(attachment.value) ? attachment.value : [attachment.value];
          items.push({ label: field.label, imageUrls: urls });
        }
      } else if (field.type === 'multi_select') {
        const checkboxEntry = selectedCheckboxes.find(c => c.input_id === field.id);
        if (checkboxEntry?.value?.length) {
          const labels = checkboxEntry.value
            .map(v => {
              const key = Object.keys(v)[0];
              const option = field.data?.options?.find(o => o.key === key);
              return option?.label || key;
            })
            .join(', ');
          items.push({ label: field.label, value: labels });
        }
      } else if (field.type === 'select') {
        if (controlValue) {
          const selectedItem = field.items?.find(i => i.value === controlValue);
          let displayValue = selectedItem?.viewValue || controlValue;
          if (!selectedItem?.responseValue) {
            const textAreaValue = formValue?.[`${field.id?.toString()  }textArea`];
            if (textAreaValue) {
              displayValue = textAreaValue;
            }
          }
          items.push({ label: field.label, value: displayValue });
        }
      } else if (field.type === 'text' || field.type === 'text_area') {
        if (controlValue) {
          items.push({ label: field.label, value: controlValue });
        }
      }
    }

    return items;
  }
}
